/**
 * D3 uses the enter/update/exit pattern:
 * - enter -> how to create new nodes
 * - update -> how to update existing nodes
 * - exit -> how to remove existing nodes
 *
 * When configuring the node, there is a common function signature:
 * `configureXXX(enter, node)`
 * Use `enter` to add "static" elements, i.e. elements that always will be there
 * and which you can modify later using `node.select(...)`
 * Use `node` to modify existing elements or to add new sub-group of elements, e.g.,
 * `node.selectAll(...).data(...)`. For this sub-group you could then have the
 * same pattern:
 * ```
 * let subNode = nodeUpdate.selectAll("g.some-selector").data([some, data, arr]);
 * const subNodeEnter = subNode.enter().append("g").classed(".some-selector", true);
 * subNodeEnter.exit().remove();
 * subNode = subNode.merge(subNodeEnter);
 * ```
 *
 * D3 Tree is by default oriented top->down, however in our case we need it to be
 * left->right. That's why we reverse the x,y coordinates below in many places
 * when positioning nodes and links between them.
 */

/**
 * Terminology
 *
 * +-------------------+ 27%                         +-------------------+
 * | ItemA         30% | (-) ----------(1)---------- | ItemB         ..% |
 * +- - - - - - - - - -+                         ____+- - - - - - - - - -+
 * | ContributorA1 10% |               +--------|_34_| ContributorB1 ..% |
 * | ContributorA2 ..% |____           |             +-------------------+
 * | ContributorA3 ..% |_67_|<---(2)---+
 * | ContributorA4 ..% |
 * | ContributorA5 ..% | (-) ---------+
 * +-------------------+              |              +-------------------+
 *                                    +-----(3)----- | ItemC         ..% |
 *                                                   +- - - - - - - - - -+
 *                                                   | ContributorC1 ..% |
 *                                                   +-------------------+
 *
 * (1) ItemB supports ItemA
 * (2) Cross-contributor link: ContributorB1 contributes to the value
 *     of ContributorA3 (cascade) with a given weight; ContributorB1
 *     has value of 34, which is calculated to 67 for ContributorA3
 * (3) ItemC supports or contributes to ContributorA5
 *
 * Each item has progress (attainment). So does each contributor. Item's progress
 * is average of its contributors'.
 */
import { transition as d3Transition, easeCubicOut } from "d3";
import { ascending } from "d3-array";
import { min } from "d3-array";
import { Dispatch, dispatch } from "d3-dispatch";
import { D3DragEvent, drag } from "d3-drag";
import { HierarchyPointLink, hierarchy, tree } from "d3-hierarchy";
import { BaseType, Local, Selection, local, select, selectAll } from "d3-selection";
import "d3-transition";
import { D3ZoomEvent, ZoomBehavior, zoom, zoomIdentity } from "d3-zoom";
import smoothscroll from "smoothscroll-polyfill";
import deletedAvatarURL from "wwwroot/img/avatars/unknown.svg";
import { IAssignee, IDeletedAssignee, isDeletedAssignee } from "@gtmhub/assignees";
import { localize } from "@gtmhub/localization";
import { isIEBrowser } from "@gtmhub/shared/utils";
import { ITreeContext } from "../models";
import {
  ICrossContributorLink,
  IItem,
  IItemId,
  IItemParent,
  IItemRearrangeAllowed,
  IItemRearrangeEventArgs,
  ISvgSize,
  ITreeMargin,
  IZoomRange,
  ItemFilter,
  ItemType,
  OwnerToPreview,
  TreeEvent,
  TreeEventListener,
  TreeViewMode,
  everyItem,
  treeEvents,
} from "./models";
import {
  IContributorSubnode,
  ICoordinates,
  ICrossContributorLinkEnd,
  IMergedCrossContributorLink,
  IMyHierarchyPointNodeTitle,
  IRenderingConstants,
  IRootNode,
  IShowMoreNode,
  MyHierarchyPointNode,
  MyHierarchyPointNodeSelection,
  MySelection,
  Node,
  RootNodeType,
  WrapTitleConstants,
  childrenCount,
  createRootDataNode,
  cssClass,
  itemEquals,
  nodeEquals,
  percentageText,
  rendering,
  wrapTitle,
} from "./util";

// Safari is the only modern browser not supporting smooth scroll!
smoothscroll.polyfill();

export const alignmentTreeRendererFactory = {
  create(treeContainer: HTMLElement, options: { x: number; y: number }): IAlignmentTreeRenderer {
    return new AlignmentTreeRenderer(treeContainer, options);
  },
};

export interface IAlignmentTreeRenderer {
  setZoomRatio(ratio: number): this;
  refresh(): void;
  getNodeBoundingClientRect(id: IItemId): DOMRect;
  getNodeOverlayBoundingClientRect(id: IItemId): DOMRect;
  getNumberOfTitleLines(id: IItemId): number;
  getContributorTitleLineHeight(): number;
  scrollIntoView(id: IItemId, contributorIdx?: number): Promise<void>;
  render(itemNodes: IItem[], treeContext: ITreeContext): void;
  setItemRearrangeEnabled(enabled: boolean): this;
  resetPosition(params?: { shouldCenterFiltered: boolean }): void;
  changeZoom(ratio: number): this;
  setZoomEnabled(enabled: boolean): this;
  setDraggable(draggable: boolean): this;
  setViewMode(viewMode: TreeViewMode): this;
  destroy(): void;
  on(name: TreeEvent, listener: TreeEventListener | null): this;
  setZoomRange(range: IZoomRange): this;
  setIsDragOrPointerNavigationEnabled(dragOrPointerNavigationEnabled: boolean): this;
  setAggregatedAttainmentVisibility(visible: boolean): this;
  setContextMenuVisibility(visible: (item: IItem) => boolean): this;
  setItemRearrangeAllowed(filter: IItemRearrangeAllowed): this;
  setStyle(id: IItemId, opts: { childSelector?: string; css: { property: string; value: string }; animation?: { duration: number; delay?: number } }): void;
}

interface IRenderingOptions {
  hierarchyUpdated: boolean;
  itemsUpdated: boolean;
}

interface IFunctionExecutionCache {
  args: unknown[];
  result?: unknown;
}

const animationDuration = 300;
export const animationDurationTimeout = animationDuration + 100;
const attainmentCircleRadius = 8;
const defaultOffset = 50;
const offsetToPreventScrollbars = 10;

const rollUpWidth = (value: string): number => value.length * 6.2 + 20;

const contributorId = (c: IItemId): string => `contributor-${c.type}-${c.id}`;

const itemNodeId = (item: IItemId) => `node-${item.type}-${item.id}`;

const nodeId = (n: Node) => (n.type === "root" || n.type === "show-more" ? `node-${n.id}` : itemNodeId(n));

const crossContributorLinkId = (item: IItem, link: ICrossContributorLink) =>
  link.direction === "outbound" ? `${item.type}-${item.id}--->${link.type}-${link.id}` : `${link.type}-${link.id}--->${item.type}-${item.id}`;

const contributorExpandToggleText = (item: IItem): string => (item.contributors.length ? "+" : item.supporters.length.toString());

const aggregatedAttainmentText = (item: IItem) => percentageText(item.fullAggregatedAttainment.value);

const contributorIconCache: Local<IFunctionExecutionCache> = local();
const itemIconCache: Local<IFunctionExecutionCache> = local();

export class AlignmentTreeRenderer implements IAlignmentTreeRenderer {
  private rootDataNode?: IRootNode;
  private svg?: Selection<SVGElement, unknown, null, undefined>;
  private mainGroup?: Selection<SVGGElement, unknown, null, undefined>;
  private draggingItem?: IItem;
  private droppedOverItem?: IItem;
  private dispatcher: Dispatch<IItem>;
  private zoom?: ZoomBehavior<Element, unknown>;
  private zoomEnabled: boolean;
  private zoomRange: IZoomRange;
  private dragOrPointerNavigationEnabled: boolean;
  private zoomRatio: number;
  private viewMode: TreeViewMode;
  private constants: IRenderingConstants;
  private margin: ITreeMargin;
  private aggregatedAttainmentVisible: boolean;
  private contextMenuVisible: ItemFilter;
  private itemRearrangeEnabled: boolean;
  private itemRearrangeAllowed: IItemRearrangeAllowed;
  private visitedCrossContributorLinks: { [key: string]: boolean };
  private currentScroll: { top: number; left: number } = {
    top: 0,
    left: 0,
  };
  private extendedSvgSize: { top: number; left: number } = {
    top: 0,
    left: 0,
  };

  private hierarchyRoot: MyHierarchyPointNode<Node>;
  private wrapCache: { [key: string]: IFunctionExecutionCache } = {};
  private options: {
    x: number;
    y: number;
  };
  private treeContext: ITreeContext;

  get calculatedDefaultOffset() {
    return this.zoomRatio * defaultOffset;
  }

  constructor(private treeContainer: HTMLElement, options: { x: number; y: number }) {
    this.options = options;
    this.dispatcher = dispatch<IItem>(...treeEvents);
    this.setContextMenuVisibility(everyItem);
    this.setItemRearrangeAllowed({ start: everyItem, end: everyItem });
    this.setItemRearrangeEnabled(false);
    this.setZoomRange({ min: 0.5, max: 1 });
    this.setZoomRatio(1);
    this.setZoomEnabled(false);
    this.setViewMode("compact");
    this.setMargin({});
  }

  public setStyle(id: IItemId, opts: { childSelector?: string; css: { property: string; value: string }; animation?: { duration: number; delay?: number } }) {
    let item;
    if (id.type === "goal") {
      item = opts.childSelector ? this.selectItemNode(id).select(opts.childSelector) : this.selectItemNode(id);
    } else {
      item = this.selectContributorWithinItemNode(id).select(".contributor-pad");
    }

    if (opts.animation) {
      const animation = opts.animation.delay
        ? d3Transition().duration(opts.animation.duration).delay(opts.animation.delay)
        : d3Transition().duration(opts.animation.duration);
      item.transition(animation).style(opts.css.property, opts.css.value);
    } else {
      item.style(opts.css.property, opts.css.value);
    }
  }

  public on(name: TreeEvent, listener: TreeEventListener | null): this {
    this.dispatcher.on(name, listener);
    return this;
  }

  public setItemRearrangeAllowed(filter: IItemRearrangeAllowed): this {
    this.itemRearrangeAllowed = filter;
    return this;
  }

  public setItemRearrangeEnabled(enabled: boolean): this {
    this.itemRearrangeEnabled = enabled;
    return this;
  }

  public setMargin(margin: ITreeMargin): this {
    this.margin = margin;
    return this;
  }

  public setAggregatedAttainmentVisibility(visible: boolean): this {
    this.aggregatedAttainmentVisible = visible;
    return this;
  }

  public setContextMenuVisibility(visible: (item: IItem) => boolean): this {
    this.contextMenuVisible = visible;
    return this;
  }

  public setZoomRatio(ratio: number): this {
    this.zoomRatio = ratio;
    return this;
  }

  public setZoomRange(range: IZoomRange): this {
    this.zoomRange = range;
    return this;
  }

  // eslint-disable-next-line @foxglove/no-boolean-parameters
  public setIsDragOrPointerNavigationEnabled(dragOrPointerNavigationEnabled: boolean): this {
    this.dragOrPointerNavigationEnabled = dragOrPointerNavigationEnabled;
    return this;
  }

  // eslint-disable-next-line @foxglove/no-boolean-parameters
  public setZoomEnabled(enabled: boolean): this {
    this.zoomEnabled = enabled;
    return this;
  }

  // eslint-disable-next-line @foxglove/no-boolean-parameters
  public setDraggable(draggable: boolean): this {
    if (draggable === this.zoomEnabled) {
      return this;
    }

    this.setZoomEnabled(draggable);

    if (this.zoomEnabled) {
      this.currentScroll = {
        left: this.treeContainer.scrollLeft || 0,
        top: this.treeContainer.scrollTop || 0,
      };
    }

    this.resizeSvg();
    this.updateSvg();
    return this;
  }

  public setViewMode(viewMode: TreeViewMode): this {
    this.viewMode = viewMode;
    this.constants = rendering()[viewMode];
    return this;
  }

  public isRendered(): boolean {
    return !!this.mainGroup;
  }

  public refresh(): void {
    if (!this.isRendered()) {
      throw new Error("you must first render the tree");
    }

    this.renderInternal({ hierarchyUpdated: true, itemsUpdated: true });
  }

  public destroy(): void {
    if (this.isRendered()) {
      this.svg.remove();

      select(window).on("resize.resizesvg", null);
      treeEvents.forEach((name) => this.on(name, null));
    }
  }

  public render(itemNodes: IItem[], treeContext: ITreeContext): void {
    if (!itemNodes) return;
    // as we are building a tree, we need a root, albeit a fake one,
    // to point to the provided nodes
    this.treeContext = treeContext;
    this.rootDataNode = createRootDataNode(itemNodes);
    this.renderInternal({ hierarchyUpdated: true, itemsUpdated: true });
  }

  public changeZoom(ratio: number) {
    this.setZoomRatio(ratio);
    this.resizeSvg();

    let left = this.calculatedDefaultOffset + this.extendedSvgSize.left;
    let top = this.calculatedDefaultOffset + this.extendedSvgSize.top;
    if (this.zoomEnabled) {
      left = this.currentScroll.left;
      top = this.currentScroll.top;
    }

    this.mainGroup
      .transition()
      .duration(animationDuration)
      .ease(easeCubicOut)
      .attr("transform", `translate(${left}, ${top}) scale(${this.zoomRatio})`)
      .end()
      .finally(() => {
        if (this.zoom) {
          const transform = zoomIdentity.translate(left, top).scale(this.zoomRatio);
          this.svg.call(this.zoom.transform, transform).call(this.zoom);
        }
        this.createTree({ hierarchyUpdated: false, itemsUpdated: false });
        this.mainGroup.classed("simplified", () => this.expandSessionColorIndicator()).classed("enabledDrag", this.itemRearrangeEnabled);
      });
    return this;
  }

  public resetPosition(params = { shouldCenterFiltered: false }): void {
    if (!this.isRendered()) {
      throw new Error("you must first render the tree");
    }

    // center the first item
    let firstNode = this.mainGroup.selectAll<SVGGElement, MyHierarchyPointNode<IItem>>("g.node").data()[1];

    // center the first matching item if such exists(filters applied) or the first containing matching contributors or simply center the first item
    if (params.shouldCenterFiltered) {
      firstNode =
        this.mainGroup
          .selectAll<SVGGElement, MyHierarchyPointNode<IItem>>("g.node")
          .data()
          .find((node) => (node.data?.matchesFilter || node.data?.contributors?.some((c) => c.matchesFilter)) && !node.data?.hidden) ||
        this.mainGroup.selectAll<SVGGElement, MyHierarchyPointNode<IItem>>("g.node").data()[1];
    }

    if (firstNode) {
      // if this.zoomEnabled is false => scrollbars are enabled.
      if (this.zoomEnabled) {
        const left = -firstNode.y * this.zoomRatio + defaultOffset;
        const top = -firstNode.x * this.zoomRatio + defaultOffset;
        this.moveSVGToGivenCoordinates(left, top);
      } else {
        const left = firstNode.y * this.zoomRatio - defaultOffset;
        const top = firstNode.x - defaultOffset;
        this.moveSVGToGivenCoordinates(this.calculatedDefaultOffset, this.calculatedDefaultOffset).then(() => {
          this.treeContainer.scrollTo({ left, top, behavior: "smooth" });
        });
      }

      if (this.dragOrPointerNavigationEnabled) {
        this.createTree({ hierarchyUpdated: false, itemsUpdated: false });
        this.mainGroup.classed("simplified", () => this.expandSessionColorIndicator()).classed("enabledDrag", this.itemRearrangeEnabled);
      }
    }

    this.resizeSvg().then(() => {
      this.extendedSvgSize = {
        left: 0,
        top: 0,
      };
    });
  }

  public scrollIntoView(id: IItemId, contributorIdx = 0): Promise<void> {
    const el = this.selectItemNode(id);
    //needed to scroll down to individual KRs
    const contributorPadHeight = 42;
    const topOffset = contributorIdx * contributorPadHeight;

    // if this.zoomEnabled is false => scrollbars are enabled.
    if (this.zoomEnabled) {
      const { y, x } = el.datum();
      const left = -y + 450;
      const top = -(x + topOffset) + 300;

      return this.moveSVGToGivenCoordinates(left, top);
    }

    return new Promise((resolve) => {
      // setTimeout with ms is required if scrollbars are enabled.
      setTimeout(() => {
        const element = el.node();
        const { x, y, height } = element.getBoundingClientRect();
        const overlayMessageHeight = 140;

        const left = x - this.constants.xCompensationForHiddenFakeRoot;
        const top = y - height - overlayMessageHeight - topOffset;

        this.treeContainer.scrollTo({ left, top, behavior: "auto" });

        let scrollingTimeout: ReturnType<typeof setTimeout>;
        const onScrollStopped = () => {
          this.treeContainer.removeEventListener("scroll", handleScrolling, false);
          return resolve();
        };

        const handleScrolling = () => {
          //We set a delayed timeout function to run a few milliseconds after the event,
          //but with each scroll event we clear that timeout function so it doesn’t run.
          //When scrolling has stopped, the delayed function doesn’t get cleared and runs.
          clearTimeout(scrollingTimeout);

          scrollingTimeout = setTimeout(onScrollStopped, 50);
        };

        this.treeContainer.addEventListener("scroll", handleScrolling, false);

        // In case no scrolling needs to be performed (i.e. element is exactly in this position)
        // we need a single starting/ending manually simulated call of the onscroll event handler.
        // A pretty rare corner case that might occur some time.
        handleScrolling();
      }, 100);
    });
  }

  public getNodeBoundingClientRect(id: IItemId): DOMRect {
    const element = this.selectItemNode(id).select(".pad").node() as Element;
    return element ? element.getBoundingClientRect() : null;
  }

  public getNodeOverlayBoundingClientRect(id: IItemId): DOMRect {
    const element = (
      id.type === "goal" ? this.selectItemNode(id).select(".item").select(".item-pad").node() : this.selectContributorWithinItemNode(id).select(".contributor-pad").node()
    ) as Element;
    return element ? element.getBoundingClientRect() : null;
  }

  private moveSVGToGivenCoordinates(left: number, top: number, duration = animationDuration): Promise<void> {
    // Note, this promise sometimes throws the following error in the console
    // Uncaught (in promise) undefined
    // I have debugged a bit and it seems to be related to the given `duration` somehow.
    // Since it is not breaking the UX, I have stopped investigating more.
    return this.mainGroup
      .transition()
      .duration(duration)
      .attr("transform", `translate(${left}, ${top}) scale(${this.zoomRatio})`)
      .end()
      .finally(() => {
        // reset the zoom transform, which is on the parent svg container
        if (this.zoom) {
          const transform = zoomIdentity.translate(left, top).scale(this.zoomRatio);
          this.svg.call(this.zoom.transform, transform).call(this.zoom);
        }

        if (!this.dragOrPointerNavigationEnabled) {
          this.renderInternal({ hierarchyUpdated: false, itemsUpdated: false });
        }
      });
  }

  private renderInternal(options: IRenderingOptions): void {
    if (!this.isRendered()) {
      this.initSvg();
    }

    this.updateVisitedCrossContributorLinks();
    this.createTree(options);
    this.updateSvg();
  }

  private initSvg() {
    // create the svg within the provided container
    this.svg = select(this.treeContainer).append("svg");

    // all dynamic elements (nodes, paths, etc.) will be created
    // within this main group
    this.mainGroup = this.svg.append("g").classed("main", true);

    select(window).on("resize.resizesvg", () => this.resizeSvg());
  }

  private resizeSvg(): Promise<void> {
    return new Promise((resolve) => {
      this.resizeInternal(0, resolve);
    });
  }

  private resizeInternal(n: number, cb: (value?: unknown) => void): Promise<void> {
    // when destroying the renderer, we might still be getting the
    // window.resize event before we have disconnected the listener
    if (!this.isRendered()) {
      return;
    }

    const { clientHeight, clientWidth } = this.treeContainer;
    const rect = this.mainGroup.node().getBoundingClientRect();
    const containerWidthMinusSpace = clientWidth - offsetToPreventScrollbars;
    const containerHeightMinusSpace = clientHeight - offsetToPreventScrollbars;
    const bottomSpace = 110;

    if (this.dragOrPointerNavigationEnabled) {
      const newSvgSize = this.calcNewSvgPosition();
      this.svg.attr("width", newSvgSize.width).attr("height", newSvgSize.height);
    } else {
      this.svg
        .attr("width", this.zoomEnabled ? (containerWidthMinusSpace >= 0 ? containerWidthMinusSpace : 0) : Math.max(rect.width, containerWidthMinusSpace))
        .attr(
          "height",
          this.zoomEnabled ? (containerHeightMinusSpace >= 0 ? containerHeightMinusSpace : 0) : Math.max(rect.height + bottomSpace, containerHeightMinusSpace)
        );
    }

    // call it a couple of time until the tree becomes static
    if (n < 10) {
      setTimeout(() => this.resizeInternal(n + 1, cb), 50);
    } else {
      cb();
    }
  }

  private calcNewSvgPosition(): ISvgSize {
    const { clientHeight, clientWidth } = this.treeContainer;
    const rect = this.mainGroup.node().getBoundingClientRect();
    const containerWidthMinusSpace = clientWidth - offsetToPreventScrollbars;
    const containerHeightMinusSpace = clientHeight - offsetToPreventScrollbars;
    const bottomSpace = 110;

    const newSvgSize: ISvgSize = {
      width: containerWidthMinusSpace >= 0 ? containerWidthMinusSpace : 0,
      height: containerHeightMinusSpace >= 0 ? containerHeightMinusSpace : 0,
    };

    if (!this.zoomEnabled) {
      this.extendedSvgSize = {
        top: 0,
        left: 0,
      };

      // Set the SVG size to rect size
      newSvgSize.width = rect.width;
      newSvgSize.height = rect.height;

      const currentLeftPosition = this.currentScroll.left - this.calculatedDefaultOffset;
      const currentTopPosition = this.currentScroll.top - this.calculatedDefaultOffset;

      // Calculate the SVG width
      if (currentLeftPosition > 0) {
        newSvgSize.width += currentLeftPosition;
        this.extendedSvgSize.left = currentLeftPosition;
      } else if (currentLeftPosition * -1 + clientWidth > newSvgSize.width) {
        const widthToAdd = currentLeftPosition * -1 + clientWidth - newSvgSize.width;
        newSvgSize.width += widthToAdd;
      }

      // Calcualte the SVG height
      if (currentTopPosition > 0) {
        newSvgSize.height += currentTopPosition;
        this.extendedSvgSize.top = currentTopPosition;
      } else if (currentTopPosition * -1 + clientHeight > newSvgSize.height) {
        const heightToAdd = currentTopPosition * -1 + clientHeight - newSvgSize.height;
        newSvgSize.height += heightToAdd;
      }

      newSvgSize.width = Math.max(newSvgSize.width, containerWidthMinusSpace);
      newSvgSize.height = Math.max(newSvgSize.height + bottomSpace, containerHeightMinusSpace);
    }

    return newSvgSize;
  }

  private updateSvg() {
    // attach/detach zoom behavior depending on
    // the current and desired state
    if (this.zoomEnabled) {
      this.updateSvgWhenDraggable();
    } else {
      this.updateSvgWhenScrollable();
    }

    this.mainGroup
      .classed("simplified", () => this.expandSessionColorIndicator())
      .classed("enabledDrag", this.itemRearrangeEnabled)
      .classed("no-scrollbars", this.zoomEnabled);
  }

  private updateSvgWhenDraggable() {
    if (!this.zoom) {
      this.zoom = this.initZoom();
      this.svg.call(this.zoom);

      if (this.dragOrPointerNavigationEnabled) {
        // Set the svg transform to the current coresponding scroll top/left of parent container
        const left = -(this.currentScroll.left - this.calculatedDefaultOffset - this.extendedSvgSize.left);
        const top = -(this.currentScroll.top - this.calculatedDefaultOffset - this.extendedSvgSize.top);
        this.moveSVGToGivenCoordinates(left, top, 0);
      } else {
        this.resetPosition();
      }
    }
  }

  private updateSvgWhenScrollable() {
    if (this.zoom) {
      this.svg.on(".zoom", null);
      this.zoom = null;

      if (this.dragOrPointerNavigationEnabled) {
        // Set the svg to top-left corner and align the scrolling of parent container
        this.moveSVGToGivenCoordinates(this.extendedSvgSize.left + this.calculatedDefaultOffset, this.extendedSvgSize.top + this.calculatedDefaultOffset, 0).then(() => {
          this.treeContainer.scrollTo({
            left: -(this.currentScroll.left - this.calculatedDefaultOffset),
            top: -(this.currentScroll.top - this.calculatedDefaultOffset),
            behavior: "auto",
          });
        });
      } else {
        this.resetPosition();
      }
    } else {
      // compensate for the smaller/bigger size so that we keep the
      // top-left corner at the same position
      if (this.options) {
        this.mainGroup.attr("transform", `translate(${this.options.x}, ${this.options.y}) scale(${this.zoomRatio})`);
      } else {
        this.mainGroup.attr("transform", `translate(${this.calculatedDefaultOffset}, ${this.calculatedDefaultOffset}) scale(${this.zoomRatio})`);
      }
    }
  }

  private dispatchAndStopPropagation(name: TreeEvent, evt: Event, ...args: unknown[]): void {
    evt.stopPropagation();
    evt.preventDefault();

    this.dispatch(name, evt, ...args);
  }

  private dispatch(name: TreeEvent, evt: Event, ...args: unknown[]): void {
    this.dispatcher.call(name, null, evt, ...args);
  }

  /**
   * First time the tree is rendered, we stash the initial cross-contributor
   * links, so that for new ones, we can show animation once they appear later on
   */
  private updateVisitedCrossContributorLinks() {
    const linksMap: { [key: string]: boolean } = {};

    const visitLink = (item: IItem, link: ICrossContributorLink) => {
      const id = crossContributorLinkId(item, link);
      linksMap[id] = true;
    };

    const visitItem = (item: IItem) => {
      item.crossContributorLinks.forEach((link) => visitLink(item, link));
      item.contributors.forEach(visitItem);
      item.supporters.forEach(visitItem);
    };

    this.rootDataNode.children.forEach(visitItem);

    if (this.visitedCrossContributorLinks) {
      // remove removed links, new ones will be added after the animation is over
      this.visitedCrossContributorLinks = Object.keys(this.visitedCrossContributorLinks).reduce((res, id) => (id in linksMap ? { ...res, [id]: true } : res), {});
    } else {
      this.visitedCrossContributorLinks = linksMap;
    }
  }

  private createTreeLayout() {
    // the separation function determines the distance (or space)
    // between tree nodes on the same depth > 0 (without the root)
    const separation = (a: MyHierarchyPointNode<Node>, b: MyHierarchyPointNode<Node>) => {
      // this case should not occur normally, but is added for TypeScript to adjust the typings
      if (a.data.type === "root" || b.data.type === "root") {
        return 0;
      }

      let space = 0.3; // you can put any value to adjust the space in general

      const isSameParent = nodeEquals(a.parent.data, b.parent.data);

      const mainNodeSpace = (node: MyHierarchyPointNode<Node>): number => {
        if (node.data.type === "root" || node.data.type === "show-more") {
          return 0;
        }

        if (node.data.obfuscated) {
          return 1;
        }

        const contributors = this.getVisibleContributors(node as MyHierarchyPointNode<IItem>);
        let additionalTitleLines = 0;

        for (const c of contributors) {
          const wrappedTitle = this.wrapTitle(c, true);
          additionalTitleLines += c.obfuscated ? 0 : Math.ceil(wrappedTitle.lines - 1);
        }

        return (
          (this.wrapTitle(node.data, false).lines * this.constants.itemTitleStyles.lineHeight +
            additionalTitleLines * this.constants.contributorTitleStyles.lineHeight +
            this.constants.ownersNamesHeight +
            this.constants.itemTitlePadding) /
          this.constants.rowHeight
        );
      };

      if (isSameParent) {
        space += mainNodeSpace(b);
      } else {
        space += mainNodeSpace(a);
      }

      if (a.data.type === "show-more") {
        space += 0.4;
      }

      const contributorsNodeSpace = (node: MyHierarchyPointNode<IItem>): number => {
        const contributors = node.data.contributors;
        return this.constants.showOnlyContributorsOfTypeGoal
          ? contributors.filter((c) => c.type === "goal" || (c.type === "metric" && c.supporters.length)).length
          : contributors.length;
      };

      if (this.constants.showContributors) {
        if (isSameParent) {
          if (b.data.type !== "show-more") {
            space += contributorsNodeSpace(b as MyHierarchyPointNode<IItem>);
          }
        } else {
          if (a.data.type !== "show-more") {
            space += contributorsNodeSpace(a as MyHierarchyPointNode<IItem>);
          }
        }
      }

      if ((this.viewMode !== "compact" && !b.data.obfuscated) || !b.data.obfuscated) {
        space += 0.5;
      }

      return space;
    };

    return tree<Node>().nodeSize([this.constants.rowHeight, this.constants.columnWidth]).separation(separation);
  }

  private createTree(options: IRenderingOptions): void {
    const treeLayout = this.createTreeLayout();

    const childrenSelector = (d: Node) => {
      let children: (IItem | IShowMoreNode)[] = [];
      if (d.type === "show-more") {
        return children;
      }

      const supporters = d.type === "root" ? d.children : d.supportersExpanded ? d.supporters : [];
      children = supporters.filter((c) => !c.hidden);

      const hasHiddenChildren = children.length < supporters.length;
      if (hasHiddenChildren) {
        const hiddenItemsCount = supporters.length - children.length;
        children.push(this.createShowMoreNode({ id: d.id, type: d.type }, hiddenItemsCount));
      }

      if (d.type !== "root" && this.constants.showContributors) {
        // if a contributor to this item node has contributors,
        // we add one node for the contributor itself, so that it can be expanded
        // otherwise, we add a node for each supporter of this contributor
        for (const contributor of d.contributors) {
          let contributorChildNodes = [];
          // Add 1 node if the helper node is expanded
          // different than adding a node for each supporter.
          // It is possible that we have only expanded the helper node.
          if (contributor.contributors.length && contributor.contributorSelfExpanded) {
            contributorChildNodes = [contributor];
            const contributorNonHiddenChildNodes = contributorChildNodes.filter((item) => !item.hidden);
            children.push(...contributorNonHiddenChildNodes);

            if (contributorNonHiddenChildNodes.length < contributorChildNodes.length) {
              const hiddenItemsCount = contributorChildNodes.length - contributorNonHiddenChildNodes.length;

              children.push(this.createShowMoreNode({ id: contributor.id, type: contributor.type, contributor }, hiddenItemsCount));
            }
          } else {
            // Add a node for each supporter
            if (contributor.supportersExpanded) {
              contributorChildNodes = [...contributor.supporters];
              const contributorNonHiddenChildNodes = contributorChildNodes.filter((item) => !item.hidden);
              children.push(...contributorNonHiddenChildNodes);

              if (contributorNonHiddenChildNodes.length < contributorChildNodes.length) {
                const hiddenItemsCount = contributorChildNodes.length - contributorNonHiddenChildNodes.length;
                children.push(this.createShowMoreNode({ id: contributor.id, type: contributor.type, contributor }, hiddenItemsCount));
              }
            }
          }
        }
      }

      return children;
    };

    if (options.hierarchyUpdated || !this.hierarchyRoot) {
      this.hierarchyRoot = treeLayout(hierarchy<Node>(this.rootDataNode, childrenSelector));
    }
    const root: MyHierarchyPointNode<Node> = this.hierarchyRoot;

    // nodes
    const nodes = root.descendants();
    const yTopmost = min(root.leaves(), (d) => d.x) - 30;

    nodes.forEach((d) => {
      // wrap the title by splitting it into smaller parts
      d.title = d.data.type !== "root" ? this.wrapTitle(d.data, false) : { wrap: [], lines: 0 };

      // adjust how stretched the tree is, i.e.
      // how long the links between nodes are
      d.y = d.depth * this.constants.columnWidth;

      // move each node to the left to compensate for the hidden root
      d.y -= this.constants.xCompensationForHiddenFakeRoot - (this.margin.left || 0);

      // move each node down so that the topmost & rightmost leaf is visible
      d.x -= yTopmost - (this.margin.top || 0);
    });

    this.configureNodes(nodes, options);
    // links between nodes
    const links = root.links();
    this.configureLinks(links);
  }

  private configureLinks(links: HierarchyPointLink<Node>[]) {
    const link = this.mainGroup.selectAll("path.link").data(links);
    const linkEnter = link
      .enter()
      .insert("path", "g")
      .classed("link", true)
      .attr("d", (d) => this.itemNodeOrContributorCurve(d));

    // link exit
    link.exit().transition().duration(animationDuration).attr("opacity", 0).remove();

    // link update
    link
      .merge(linkEnter)
      // hide links coming from the fake root node
      .attr("opacity", (d) => (d.source.data.type === "root" ? 0 : 1))
      .transition()
      .duration(animationDuration)
      .attr("d", (d) => this.itemNodeOrContributorCurve(d));
  }

  private configureNodes(nodes: MyHierarchyPointNode<Node>[], options: IRenderingOptions): void {
    let node = this.mainGroup.selectAll<SVGGElement, MyHierarchyPointNode<Node>>("g.node").data(nodes, (d) => nodeId(d.data));

    const nodeEnter = node
      .enter()
      .append("g")
      .classed("node", true)
      .classed("show-more", (d) => d.data.type === "show-more")
      .attr("id", (d) => nodeId(d.data))
      .attr("transform", (d) => `translate(${d.y}, ${d.x})`)
      // hide fake root node
      .attr("opacity", (d) => (d.data.type === "root" ? 0 : 1));

    // node exit
    node
      .exit<MyHierarchyPointNode<Node>>()
      .transition()
      .duration(animationDuration)
      .attr("transform", (d) => `translate(${d.y - 30}, ${d.x})`)
      .attr("opacity", 0)
      .remove();

    // node update
    node = node.merge(nodeEnter);
    node
      .transition()
      .duration(animationDuration)
      .attr("transform", (d) => `translate(${d.y}, ${d.x})`)
      .selection()
      .classed("matched", (d) => d.data.type !== "root" && d.data.type !== "show-more" && d.data.matchesFilter)
      .classed("highlighted", (d) => d.data.type !== "root" && d.data.type !== "show-more" && (d.data.located || d.data.highlighted))
      .classed("pinned", (d) => d.data.type !== "root" && d.data.type !== "show-more" && d.data.pinned);

    const nonRootNodeEnter = nodeEnter.filter((d) => d.data.type !== "root") as MyHierarchyPointNodeSelection<IShowMoreNode | IItem>;
    const nonRootNodeUpdate = node.filter((d) => d.data.type !== "root") as MyHierarchyPointNodeSelection<IShowMoreNode | IItem>;

    // only non-root and non-showmore nodes
    const nonShowMoreNonRootNodeEnter = nonRootNodeEnter.filter((d) => d.data.type !== "show-more") as MyHierarchyPointNodeSelection<IItem>;
    const nonShowMoreNonRootNodeUpdate = nonRootNodeUpdate.filter((d) => d.data.type !== "show-more") as MyHierarchyPointNodeSelection<IItem>;

    if (options.itemsUpdated) {
      this.configurePad(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
      this.configureSessionColorIndicator(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
      this.configureItem(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
      this.configurePrivateNote(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
      this.configureContributors(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
      const showMoreEnter = nonRootNodeEnter.filter((n) => n.data.type === "show-more");
      const showMoreUpdates = nonRootNodeUpdate.filter((d) => d.data.type === "show-more");
      this.configureItemTitle(showMoreEnter, showMoreUpdates);
    }
    // when the nodes get too small they are covered with the session's color box
    // it is rendered as a separate element on top of everything else so we don't have to update all other elements
    this.configureSessionColorOverlay(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate);
    if (options.itemsUpdated) {
      this.configureRearrange(nonShowMoreNonRootNodeEnter, nonShowMoreNonRootNodeUpdate, (d) => d.data, { contributor: false });
    }

    // triggering the resizeSvg once we have the whole SVG ready so SVG width attr is valid since the start
    node.call(this.resizeSvg.bind(this));
  }

  private configureItem(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    let item = node.selectAll("g.item").data((d) => [d]);

    const itemEnter = item.enter().append("g").classed("item", true);

    itemEnter.exit().remove();

    item = item.merge(itemEnter);
    item.classed("unmatched", (d) => this.treeContext?.areFiltersApplied && !d.data.matchesFilter && !d.data.located && !d.data.highlighted);

    this.configureItemPad(itemEnter, item);
    this.configureIcon(itemEnter, item);
    this.configureItemOwnersNames(item);
    this.configureItemOwnersIcons(item);

    this.configureContextMenu(itemEnter, item);
    this.configureAttainment(itemEnter, item);
    this.configureWorkflow(itemEnter, item);
    this.configureItemTitle(itemEnter, item);

    this.configureAggregatedAttainment(itemEnter, item);
    this.configureExpandToggle(itemEnter, item);
  }

  private configureItemPad(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const width = this.constants.itemWidth;

    enter
      .append("rect")
      .classed("item-pad", true)
      .attr("node", (d) => d.id)
      .attr("y", 0)
      .attr("x", 0)
      .attr("rx", 5)
      .attr("ry", 5);

    const horizontalBorderOffset = 10; // 10 = 2(top and down border)x3(border width) when item matches filter + 4 highlight for shared Objective
    node
      .select("rect.item-pad")
      .attr("width", width - 6) //-6 so it does not overlap with the node pad rect borders
      .attr("height", (d) => this.itemPadHeight(d) - horizontalBorderOffset)
      .attr("transform", "translate(3,3)") // so it does not overlap with the node pad rect borders
      .attr("data-test-id", "objective-highlight-border");
  }

  private configureContributorPad(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const width = this.constants.itemWidth;

    enter.append("rect").classed("contributor-pad", true).attr("y", 0).attr("x", 0).attr("rx", 5).attr("ry", 5);

    node
      .select("rect.contributor-pad")
      .attr("width", width - 6) //-6 so it does not overlap with the node pad rect borders
      .attr("height", (d) => `${this.contributorPadHeight(d)}px`)
      .attr("transform", "translate(-7, -20)")
      .attr("data-test-id", "key-result-highlight-border")
      .style("mix-blend-mode", "multiply"); // required for border highlights to be visible
  }

  private configureContributors(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const contributors = node.selectAll("g.contributors").data((d) => (this.constants.showContributors && d.data.contributors.length ? [d] : []));

    const contributorsEnter = contributors.enter().append("g").classed("contributors", true);
    contributors.exit().remove();

    const contributorsUpdate = contributors.merge(contributorsEnter).attr("transform", (d) => {
      const offsetBetweenNodes = 20;
      const additionalItemTitleLines = d.data.obfuscated ? 0 : Math.ceil(d.title.lines - 1);
      const nodeContentTopVerticalOffset = d.data.obfuscated ? 0 : this.constants.contentHeightOffset;
      const ownersNamesHeight = d.data.obfuscated ? 0 : this.constants.ownersNamesHeight;
      const y =
        additionalItemTitleLines * this.constants.itemTitleStyles.lineHeight +
        this.constants.rowHeight +
        offsetBetweenNodes +
        nodeContentTopVerticalOffset +
        ownersNamesHeight;
      return `translate(10, ${y})`;
    });

    // in "compact" viewMode metric contributors with supporters remain visible to keep the structure
    let contributor = contributorsUpdate.selectAll<SVGGElement, MyHierarchyPointNode<IItem>>("g.contributor").data((d) => {
      const contributors = this.getVisibleContributors(d);

      let previousAdditionalTitleLines = 0;
      const data = [];

      for (const c of contributors) {
        data.push({ node: d, contributor: c, previousAdditionalTitleLines });

        if (!c.obfuscated) {
          const wrappedTitle = this.wrapTitle(c, true);
          previousAdditionalTitleLines += Math.ceil(wrappedTitle.lines - 1);
        }
      }
      previousAdditionalTitleLines = 0;
      return data;
    });

    const contributorEnter = contributor
      .enter()
      .append("g")
      .classed("contributor", true)
      .classed("metric", (d) => d.contributor.type === "metric")
      .classed("goal", (d) => d.contributor.type === "goal")
      .attr("id", (d) => contributorId(d.contributor));

    // in "compact" viewMode metrics have opacity=0.5
    contributor.classed("faded", (d) => this.constants.showOnlyContributorsOfTypeGoal && d.contributor.type === "metric");

    contributor.exit().remove();

    contributor = contributor.merge(contributorEnter);
    contributor.attr("opacity", (d) => (d.contributor.contributors.length && d.contributor.contributorSelfExpanded ? 0.5 : 1));
    contributor.classed("matched", (d) => this.shouldDisplayContributorAsMatched(d));
    contributor.classed("unmatched", (d) => this.shouldDisplayContributorAsUnmatched(d));
    contributor.attr("transform", (d, i) => {
      return `translate(0, ${i * this.constants.rowHeight + d.previousAdditionalTitleLines * this.constants.contributorTitleStyles.lineHeight})`;
    });

    this.configureContributorPad(contributorEnter, contributor);
    this.configureContributorIcon(contributorEnter, contributor);
    this.configureContributorContextMenu(contributorEnter, contributor);
    this.configureContributorAttainment(contributorEnter, contributor);
    this.configureContributorSeparator(contributorEnter, contributor);
    this.configureContributorTitle(contributorEnter, contributor);
    this.configureContributorOwnersIcons(contributor);
    this.configureContributorPrivateNote(contributorEnter, contributor);
    this.configureContributorExpandToggle(contributorEnter, contributor);
    this.configureRearrange(contributorEnter, contributor, (d) => d.contributor, { contributor: true });
    this.configureCascadingHoverIcon(contributorEnter, contributor);
  }

  private configureContributorExpandToggle(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const toggle = enter
      .append("g")
      .classed("togglerEnter", true)
      .attr("cursor", "pointer")
      .on("click", (evt: Event, d) => {
        if (d.contributor.contributors.length) {
          // toggle helper node
          this.dispatchAndStopPropagation("toggleItemSelf", evt, d.contributor);
        } else {
          // toggle supporter nodes
          this.dispatchAndStopPropagation("toggleItemSupporters", evt, d.contributor);
        }
      });
    toggle.append("rect").classed("expander", true).attr("ry", 10).attr("rx", 10).attr("height", 20).attr("transform", "translate(-10, -10)");
    toggle.append("text").classed("plus", true).attr("transform", "translate(-4, 4)");
    toggle.append("text").classed("minus", true).attr("transform", "translate(-3, 5)").text("-");

    const toggleUpdate = node.select("g.togglerEnter");
    toggleUpdate
      .attr("class", (d) =>
        cssClass({
          togglerEnter: true,
          hide: !childrenCount(d.contributor),
          collapsed: d.contributor.contributors.length ? !d.contributor.contributorSelfExpanded : !d.contributor.supportersExpanded,
          expanded: d.contributor.contributors.length ? d.contributor.contributorSelfExpanded : d.contributor.supportersExpanded,
        })
      )
      .attr("transform", `translate(${this.constants.itemWidth + 5}, 0)`);
    toggleUpdate.select("text.plus").text((d) => contributorExpandToggleText(d.contributor));
    toggleUpdate.select("rect.expander").attr("width", (d) => (!d.contributor.supportersExpanded ? 13 + contributorExpandToggleText(d.contributor).length * 7 : 20));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  private configureContributorIcon(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const contributorIcon = enter
      .append("image")
      .classed("subitem-icon", true)
      .attr("width", 16)
      .attr("height", 16)
      .attr("transform", () => `translate(5,-5)`);

    const icons = contributorIcon.style("display", this.viewMode === "relaxed" || "block");

    icons
      .filter((d, i, nodes) => {
        const prevUpdate = contributorIconCache.get(<Element>nodes[i]);
        return !prevUpdate || d.contributor.type !== prevUpdate.args[0] || d.contributor.inboundCrossContributorLinksAllowed !== prevUpdate.args[1];
      })
      .each((d, i, nodes) => {
        contributorIconCache.set(<Element>nodes[i], { args: [d.contributor.type, d.contributor.inboundCrossContributorLinksAllowed] });
      })
      .attr("xlink:href", (d) => {
        const contributorTypesIcons: Record<ItemType, string> = {
          goal: require("wwwroot/img/icons/goal.svg"),
          metric: require("wwwroot/img/icons/metric.svg"),
        };

        return d.contributor.crossContributorLinks.some((contributor) => contributor.direction === "outbound")
          ? require("wwwroot/img/icons/contributing-metric.svg")
          : contributorTypesIcons[d.contributor.type];
      });
  }

  /**
   * Determine if the contributor needs to be styled as matched (highlighted in yellow)
   * Contributor is highlighted if it is of type goal (child OKR) and not expanded
   */
  private shouldDisplayContributorAsMatched(d: IContributorSubnode): boolean {
    return d.contributor.type !== "metric" && d.contributor.matchesFilter && !(d.contributor.contributors.length && d.contributor.contributorSelfExpanded);
  }

  /**
   * Determine if the contributor needs to be styled as unmatched (faded out)
   * A contributor is faded out when the parent node is not located or highlighted and:
   * 1. it's of type metric and the parent OKR doesn't match the filters
   * 2. it's of type goal (child OKR), matches the filters but the parent OKR doesn't match the filters
   */
  private shouldDisplayContributorAsUnmatched(d: IContributorSubnode): boolean {
    return (
      this.treeContext?.areFiltersApplied &&
      ((d.contributor.type === "metric" && !d.node.data.matchesFilter) ||
        (d.contributor.type === "goal" && !d.contributor.matchesFilter && !d.node.data.matchesFilter)) &&
      !d.node.data.located &&
      !d.node.data.highlighted
    );
  }

  private configureCascadingHoverIcon(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>): void {
    const cascadingIcon = enter.append("g").classed("cascadingIcon", true).attr("cursor", "pointer").append("image");

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    cascadingIcon.attr("xlink:href", require("wwwroot/img/icons/cascading-metric-alignment.svg"));
    cascadingIcon.attr("transform", "translate(-5, -5)").attr("x", this.constants.itemWidth);

    const cascadingIconUpdate = node.select("g.cascadingIcon");
    cascadingIconUpdate
      .attr("class", (d) =>
        cssClass({
          cascadingIcon: true,
          hide: !d.contributor.crossContributorLinks.some((contributor) => contributor.direction === "inbound"),
        })
      )
      .call(this.crossContributorCascadingLinks.bind(this));
  }

  private crossContributorCascadingLinks<GElement extends BaseType, PElement extends BaseType>(selection: Selection<GElement, IContributorSubnode, PElement, unknown>) {
    selection
      .on("mouseover", (evt, d) => {
        const mergedLinks = this.mergeCrossContributorLinks(d.node, d.contributor);
        for (const link of mergedLinks) {
          if (link.source && link.target) {
            // draw a line between the two contributors
            this.mainGroup
              .insert("path", "g")
              .attr("class", "dashed-link")
              .attr("transform", "translate(0, 8)")
              .attr("d", () => {
                return this.nodeCurve(this.getSourceCoordinates(link), this.getTargetCoordinates(link));
              });
          }
          // add left/right roll-up
          if (link.source) {
            select(`#${contributorId(link.source.contributor)}`)
              .append("g")
              .call(this.crossContributorCascadingLinkOutboundRollUp.bind(this), link.source.link.contributionValue, link.target == null);
          }

          if (link.target) {
            select(`#${contributorId(link.target.contributor)}`)
              .append("g")
              .call(this.crossContributorCascadingLinkInboundRollUp.bind(this), link.target.link.contributionValue, link.source == null);
          }
        }
      })
      .on("mouseout", () => {
        selectAll(".dashed-link").remove();
        selectAll(".rollup").remove();
      });
  }

  private getSourceCoordinates = (link: IMergedCrossContributorLink): ICoordinates => {
    const sourceCoordinates = this.findContributorCoordinates(link.target.node, link.target.contributor);
    sourceCoordinates.y += rollUpWidth(this.getContributorValue(link.target.link.contributionValue));

    return sourceCoordinates;
  };

  private getTargetCoordinates = (link: IMergedCrossContributorLink): ICoordinates => {
    const targetCoordinates = this.findContributorCoordinates(link.source.node, link.source.contributor);
    targetCoordinates.y -= rollUpWidth(this.getContributorValue(link.source.link.contributionValue));

    return targetCoordinates;
  };

  private crossContributorCascadingLinkOutboundRollUp<GElement extends BaseType, PElement extends BaseType>(
    selection: Selection<GElement, IContributorSubnode, PElement, unknown>,
    value: number,
    isOpenEnded: boolean
  ) {
    // add '+' sign before contributor's value
    const displayValue: string = (value >= 0 ? "+" : "") + this.getContributorValue(value);

    const rollUp = selection
      .attr("class", "rollup")
      .attr("transform", `translate(-${rollUpWidth(displayValue) + 12}, 5)`)
      .style("pointer-events", "none");

    if (!isOpenEnded) {
      rollUp
        .append("rect")
        .attr("fill", "#7485A2")
        .attr("rx", 5)
        .attr("ry", 5)
        .attr("transform", "translate(0, -15)")
        .attr("height", 30)
        .attr("width", rollUpWidth(displayValue));
      rollUp
        .append("text")
        .text(displayValue)
        .attr("fill", "#fff")
        .style("text-anchor", "middle")
        .attr("transform", `translate(${rollUpWidth(displayValue) / 2}, 4)`);
    }
  }

  private crossContributorCascadingLinkInboundRollUp<GElement extends BaseType, PElement extends BaseType>(
    selection: Selection<GElement, IContributorSubnode, PElement, unknown>,
    value: number,
    isOpenEnded: boolean
  ) {
    const rollUp = selection
      .attr("class", "rollup")
      .attr("transform", `translate(${this.constants.itemWidth + 5}, 0)`)
      .style("pointer-events", "none");

    rollUp
      .append("rect")
      .attr("fill", "#7485A2")
      .attr("rx", 5)
      .attr("ry", 5)
      .attr("height", 30)
      .attr("width", rollUpWidth(this.getContributorValue(value)) + 20)
      .attr("transform", "translate(-13, -10)");

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    rollUp.append("image").attr("xlink:href", require("wwwroot/img/icons/cascading-metric-alignment.svg")).attr("transform", "translate(-10, -5)");
    rollUp.append("text").text(this.getContributorValue(value)).attr("fill", "#fff").style("text-anchor", "start").attr("x", 17).attr("y", 10);

    if (isOpenEnded) {
      // draw a half-line coming into this contributor
      rollUp
        .append("line")
        .attr("width", "40")
        .attr("height", "2")
        .attr("class", "dashed-link")
        .attr("x1", "10")
        .attr("y1", "-10")
        .attr("x2", "30")
        .attr("y2", "-10")
        .attr("transform", `translate(${rollUpWidth(this.getContributorValue(value))}, 15)`);
    }
  }

  private getContributorValue = (value: number): string => {
    return value >= 1000000 ? (Math.round((value / 1000000) * 10) / 10).toString() + "m" : value.toString();
  };

  private configureContributorPrivateNote(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const privateNote = node
      .selectAll("g.private")
      .selection()
      .data((d) => {
        return d.contributor.private ? [d] : [];
      });

    const privateNoteEnter = privateNote.enter().append("g").classed("private", true);
    privateNoteEnter
      .append("text")
      .text((d) => (d.contributor.type === "goal" ? localize("private_goal") : localize("private_kr")))
      .attr("class", (d) => cssClass({ "private-badge": d.contributor.private && d.contributor.obfuscated, "private-text": !d.contributor.obfuscated }));
    privateNoteEnter
      .append("circle")
      .attr("r", 6)
      .attr("cy", 3)
      .attr("cx", -1)
      .classed("private-dot", true)
      .attr("opacity", (d) => (d.contributor.obfuscated ? 0 : null));

    privateNote
      .merge(privateNoteEnter)
      .select("text.private-badge")
      .attr("transform", `translate(${this.viewMode === "relaxed" ? this.constants.xTitle - 10 : 35}, 6)`);

    privateNote.exit().remove();
  }

  private configureItemTitleClick = (evt: MouseEvent, d): void => {
    if (d.data.type === "show-more") {
      let parent = d.parent.data as IItem | IRootNode;
      if (parent.type !== "root") {
        const isParentContributorOfItemNode = !nodeEquals(d.data.parent, parent);
        if (isParentContributorOfItemNode) {
          parent = parent.contributors.find((c) => nodeEquals(c, d.data.parent));
        }
      }
      this.dispatchAndStopPropagation("showMoreClick", evt, parent.type === "root" ? null : parent);
    } else {
      this.dispatchAndStopPropagation("itemTitleClick", evt, d.data);
    }
  };

  private toggleTooltip = (evt: MouseEvent, d: MyHierarchyPointNode<IItem>) => {
    const hideTooltip = !d.title?.overflow;

    if (hideTooltip) {
      return { hideTooltip };
    }

    const { y: yNode } = select(evt.target as Element)
      .node()
      .getBoundingClientRect();

    const tooltipArrowHeight = 5;
    const cursorleftOffset = 30;

    const x = evt.pageX - cursorleftOffset;
    const y = yNode + this.constants.itemTitleStyles.lineHeight + tooltipArrowHeight;

    return { x, y, text: d.data.title };
  };

  private configureItemTitle(enter: MyHierarchyPointNodeSelection<IShowMoreNode | IItem>, node: MyHierarchyPointNodeSelection<IShowMoreNode | IItem>) {
    const isIE = isIEBrowser();

    node
      .selectAll("g.item-title")
      .data((d) => (d.data.type === "show-more" || !d.data.private || !d.data.obfuscated ? [d] : []))
      .enter()
      .append("g")
      .classed("item-title", true)
      .on("click", this.configureItemTitleClick)
      .call(this.tooltip.bind(this), "item-title-tooltip", this.toggleTooltip)
      .append(!isIE ? "svg:foreignObject" : "text")
      .attr("data-test-id", "alignment-tree-objective-title")
      .classed("item-title-text", isIE);

    if (!isIE) {
      const text = node
        .select("g.item-title foreignObject")
        .attr("y", "4px")
        .attr("width", (d) => this.titleWidth(d.data, false))
        .attr("height", (d) => `${(d.title.overflow ? this.constants.titleWrap.maxLines : d.title.lines) * this.constants.itemTitleStyles.lineHeight}px`)
        .selectAll("div")
        .selection()
        .data((d) => [
          {
            data: d.data,
            title: d.title,
          },
        ]);

      const textEnter = text
        .enter()
        .append("xhtml:div")
        .classed("item-title-text", true)
        .style("font-size", () => `${this.constants.itemTitleStyles.fontSize}px`)
        .style("line-height", () => `${this.constants.itemTitleStyles.lineHeight}px`)
        .style("font-weight", () => `${this.constants.itemTitleStyles.fontWeight}px`);

      text
        .merge(textEnter)
        .style("width", (d) => this.titleWidth(d.data, false))
        .text((d) => d.data.title)
        .exit()
        .remove();
    } else {
      const textSpan = node
        .select("g.item-title text.item-title-text")
        .selectAll("tspan")
        .selection()
        .data((d) => d.title.wrap);

      const textSpanEnter = textSpan
        .enter()
        .append("tspan")
        .attr("dy", this.constants.itemTitleStyles.lineHeight)
        .attr("x", 0)
        .style("font-size", () => `${this.constants.itemTitleStyles.fontSize}px`)
        .style("line-height", () => `${this.constants.itemTitleStyles.lineHeight}px`)
        .style("font-weight", () => `${this.constants.itemTitleStyles.fontWeight}px`);
      textSpan.merge(textSpanEnter).text((d) => d);
      textSpan.exit().remove();
    }

    node.select(".item-title").attr("transform", (d) => {
      const showMoreItemTitleTranslateX = 5;
      const itemTitleTranslateY = 13;
      return `translate(${d.data.type === "show-more" ? showMoreItemTitleTranslateX : this.constants.xTitle}, ${itemTitleTranslateY})`;
    });
  }

  private configureContributorTitle(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const isIE = isIEBrowser();

    node
      .selectAll("g.contributor-title")
      .data((d) => (!d.contributor.obfuscated ? [d] : []))
      .enter()
      .append("g")
      .classed("contributor-title", true)
      .on("click", (evt: MouseEvent, d) => this.dispatchAndStopPropagation("itemTitleClick", evt, d.contributor))
      .append(!isIE ? "svg:foreignObject" : "text")
      .classed("contributor-title-text", isIE)
      .call(this.tooltip.bind(this), "item-title-tooltip", (evt: MouseEvent, d: IContributorSubnode) => {
        const hideTooltip = !this.wrapTitle(d.contributor, true).overflow;

        if (hideTooltip) {
          return { hideTooltip };
        }

        const { y: yNode } = select(evt.target as Element)
          .node()
          .getBoundingClientRect();

        const tooltipArrowHeight = 5;
        const cursorleftOffset = 30;

        const x = evt.pageX - cursorleftOffset;
        const y = yNode + this.constants.contributorTitleStyles.lineHeight + tooltipArrowHeight;

        return { x, y, text: d.contributor.title };
      });

    if (!isIE) {
      const text = node
        .select("g.contributor-title foreignObject")
        .attr("y", "4px")
        .attr("width", (d) => this.titleWidth(d.contributor, true))
        .attr("height", (d) => {
          const titleWrap = this.wrapTitle(d.contributor, true);
          return `${(titleWrap.overflow ? this.constants.titleWrap.maxLines : titleWrap.lines) * this.constants.contributorTitleStyles.lineHeight}px`;
        })
        .selectAll("div")
        .selection()
        .data((d) => {
          return [d];
        });

      const textEnter = text
        .enter()
        .append("xhtml:div")
        .classed("contributor-title-text", true)
        .style("font-size", () => `${this.constants.contributorTitleStyles.fontSize}px`)
        .style("line-height", () => `${this.constants.contributorTitleStyles.lineHeight}px`)
        .style("font-weight", () => `${this.constants.contributorTitleStyles.fontWeight}px`);

      text
        .merge(textEnter)
        .style("width", (d) => this.titleWidth(d.contributor, true))
        .text((d) => d.contributor.title)
        .exit()
        .remove();
    } else {
      const textSpan = node
        .select("g.contributor-title text.contributor-title-text")
        .selectAll("tspan")
        .selection()
        .data((d) => this.wrapTitle(d.contributor, true).wrap);

      const textSpanEnter = textSpan
        .enter()
        .append("tspan")
        .attr("dy", this.constants.contributorTitleStyles.lineHeight)
        .attr("x", 0)
        .style("font-size", () => `${this.constants.contributorTitleStyles.fontSize}px`)
        .style("line-height", () => `${this.constants.contributorTitleStyles.lineHeight}px`)
        .style("font-weight", () => `${this.constants.contributorTitleStyles.fontWeight}px`);
      textSpan.merge(textSpanEnter).text((d) => d);
      textSpan.exit().remove();
    }

    node.select(".contributor-title").attr("transform", () => {
      const x = 26;
      const y = -10;
      return `translate(${x}, ${y})`;
    });
  }

  private configureContributorSeparator(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    enter.append("rect").classed("contributor-separator", true).attr("height", 3).attr("transform", "translate(-8, -20)");

    node
      .select("rect.contributor-separator")
      .attr("width", this.constants.itemWidth - 2)
      .style("mix-blend-mode", "multiply"); // required for border highlights to be visible
  }

  private configureContributorAttainment(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    const textAttainment = node
      .selectAll("text.contributor-attainment")
      .selection()
      .data((d) => (typeof d.contributor.attainment.value === "number" ? [d.contributor.attainment] : []));

    const textAttainmentEnter = textAttainment.enter().append("text").classed("contributor-attainment", true);

    textAttainment.exit().remove();
    textAttainment
      .merge(textAttainmentEnter)
      .attr("x", this.constants.itemWidth - 50)
      .attr("y", () => 8)
      .attr("fill", (d) => d.color)
      .style("text-anchor", "end")
      .text((d) => percentageText(d.value));

    const colorAttainment = node
      .selectAll("circle.contributor-attainment")
      .selection()
      .data((d) => (typeof d.contributor.attainment.value === "undefined" ? [d.contributor.attainment] : []));

    const colorAttainmentEnter = colorAttainment.enter().append("circle").classed("contributor-attainment", true);

    colorAttainment.exit().remove();

    colorAttainment
      .merge(colorAttainmentEnter)
      .attr("cy", this.constants.progressLabelPositionY.contributor)
      .attr("cx", this.constants.itemWidth - 35 - this.constants.progressLabelOffsetPositionX - attainmentCircleRadius)
      .attr("r", attainmentCircleRadius)
      .attr("fill", (d) => d.color);
  }

  private configureContributorContextMenu(enter: MySelection<IContributorSubnode>, node: MySelection<IContributorSubnode>) {
    enter
      .append("text")
      .classed("contributor-dropdown-toggler", true)
      .text("...")
      .on("click", (evt: MouseEvent, d) => this.contextMenuVisible(d.contributor) && this.dispatchAndStopPropagation("contextMenuOpen", evt, d.contributor));

    node
      .select("text.contributor-dropdown-toggler")
      .attr("transform", () => `translate(${this.constants.itemWidth - 40}, 4)`)
      .attr("opacity", (d) => (this.contextMenuVisible(d.contributor) ? 1 : 0))
      .attr("data-test-id", "alignment-tree-key-result-context-menu");
  }

  private configureRearrange<Datum>(enter: MySelection<Datum>, node: MySelection<Datum>, data: (d: Datum) => IItem, opts: { contributor: boolean }) {
    const dropZoneCssClass = opts.contributor ? "contributor-dropzone" : "item-dropzone";

    // we need to store a reference to this, because when dragging, we need to use normal functions
    // as they have this bound to the correct source element
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const that = this;

    // contributors do not get an individual handle, they must be expanded
    if (!opts.contributor) {
      const handleX = -15;
      const handleY = 18;

      const rearrangeNotAllowed = node.selectAll("text.no-handle").data((d) => (!this.itemRearrangeAllowed.start(data(d)) ? [data(d)] : []));
      rearrangeNotAllowed.enter().append("text").classed("no-handle", true).text("+").attr("x", handleX).attr("y", handleY);
      rearrangeNotAllowed.exit().remove();

      const rearrangeAllowed = node.selectAll("circle.handle").data((d) => (this.itemRearrangeAllowed.start(data(d)) ? [data(d)] : []));
      const rearrangeAllowedEnter = rearrangeAllowed
        .enter()
        .append("circle")
        .classed("handle", true)
        .attr("cy", 0)
        .attr("cx", 0)
        .attr("r", 10)
        .attr("transform", `translate(${handleX}, ${handleY})`);

      rearrangeAllowed.exit().remove();
      rearrangeAllowed
        .merge(rearrangeAllowedEnter)
        .attr("fill", (d) => d.session.color)
        .call(
          drag<SVGCircleElement, IItem>()
            .on("start", function (evt: D3DragEvent<SVGCircleElement, IItem, IItem>) {
              (evt.sourceEvent as Event).stopPropagation();

              that.draggingItem = evt.subject;

              selectAll<SVGGElement, IItem>("g.dropzone").style("display", (d) => (that.itemRearrangeAllowed.end(that.draggingItem, d) ? "block" : "none"));

              select(this).attr("pointer-events", "none");
            })
            .on("drag", function (evt: D3DragEvent<SVGCircleElement, IItem, IItem>) {
              select(this)
                .attr("cx", evt.x - handleX)
                .attr("cy", evt.y - handleY);
            })
            .on("end", function (evt: D3DragEvent<SVGCircleElement, IItem, IItem>) {
              selectAll("g.dropzone").style("display", "none");
              select(this).attr("pointer-events", "").transition().duration(animationDuration).attr("cx", 0).attr("cy", 0);

              if (that.droppedOverItem && that.itemRearrangeAllowed.end(that.draggingItem, that.droppedOverItem)) {
                const args: IItemRearrangeEventArgs = {
                  item: that.draggingItem,
                  newParent: that.droppedOverItem,
                };
                that.dispatch("itemRearrange", evt.sourceEvent as Event, args);
              }

              that.draggingItem = null;
            })
        );
    }

    const dropZone = node.selectAll(`g.${dropZoneCssClass}`).data((d) => [data(d)]);
    const dropZoneEnter = dropZone.enter().append("g").classed("dropzone", true).classed(dropZoneCssClass, true).style("display", "none");
    dropZoneEnter
      .append("circle")
      .attr("r", opts.contributor ? 15 : 20)
      .attr("cy", 0)
      .attr("cx", 0)
      .attr("pointer-events", "mouseover");
    dropZoneEnter.exit().remove();

    dropZone
      .merge(dropZoneEnter)
      .select<SVGCircleElement>("circle")
      .attr("transform", opts.contributor ? `translate(${this.constants.itemWidth}, 0)` : `translate(${this.constants.itemWidth + 15}, 20)`)
      .on("mouseover", function (evt, d) {
        if (that.draggingItem) {
          that.droppedOverItem = d;
          select(this.parentElement).classed("active", true);
        }
      })
      .on("mouseout", function () {
        that.droppedOverItem = null;
        select(this.parentElement).classed("active", false);
      });
  }

  private configureExpandToggle(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const toggle = enter
      .append("g")
      .classed("togglerEnter", true)
      .attr("cursor", "pointer")
      .on("click", (evt: Event, d) => {
        this.dispatchAndStopPropagation("toggleItemSupporters", evt, d.data);
      });
    toggle.append("rect").classed("expander", true).attr("ry", 10).attr("rx", 10).attr("height", 20).attr("transform", "translate(-10, -10)");
    toggle.append("text").classed("plus", true).attr("transform", "translate(-4, 4)");
    toggle.append("text").classed("minus", true).attr("transform", "translate(-3, 5)").text("-");

    const toggleUpdate = node.select("g.togglerEnter");
    toggleUpdate
      .attr("class", (d) =>
        cssClass({
          togglerEnter: true,
          hide: !d.data.supporters.length,
          collapsed: !d.data.supportersExpanded,
          expanded: d.data.supportersExpanded,
        })
      )
      .attr("transform", `translate(${this.constants.itemWidth + 15}, ${this.constants.rowHeight / 2})`);
    toggleUpdate.select("text.plus").text((d) => d.data.supporters.length.toString());
    toggleUpdate.select("rect.expander").attr("width", (d) => (!d.data.supportersExpanded ? 13 + d.data.supporters.length.toString().length * 7 : 20));
  }

  private configureWorkflow(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const workflowBackground = node
      .selectAll("circle.workflow")
      .selection()
      .data((d) => (d.data.workflow ? [d.data.workflow.status] : []));
    const workflowBackgroundEnter = workflowBackground.enter().append("circle").classed("workflow", true).attr("cx", 28).attr("cy", 33).attr("r", 7);
    workflowBackground.merge(workflowBackgroundEnter).attr("fill", (d) => {
      switch (d) {
        case "draft":
          return "#89919B";
        case "approved":
          return "#1cb987";
        case "notEnoughReviewers":
          return "#E3541B";
        case "inReview":
          return "#817CC9";
      }
    });
    workflowBackground.exit().remove();

    let workflowIcon = node.selectAll(".workflow-status-icon").data((d) => (d.data.workflow ? [d.data.workflow.status] : []));

    const workflowIconEnter = workflowIcon
      .enter()
      .append("foreignObject")
      .attr("width", 15)
      .attr("height", 15)
      .classed("workflow-status-icon", true)
      .attr("transform", "translate(22, 25)");

    workflowIconEnter.append("xhtml:span");

    workflowIcon = workflowIcon.merge(workflowIconEnter);

    workflowIcon.select("span").attr("class", (d) => {
      switch (d) {
        case "draft":
          return "gh-font-icon-edit";
        case "approved":
          return "gh-font-icon-check";
        case "inReview":
          return "gh-font-icon-clock";
        case "notEnoughReviewers":
          return "nc-icon-mini icon users_add-27";
      }
    });

    workflowIcon.exit().remove();
  }

  private configurePrivateNote(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const privateNote = node
      .selectAll("g.private")
      .selection()
      .data((d) => (d.data.private ? [d] : []));

    const privateNoteEnter = privateNote.enter().append("g").classed("private", true);
    privateNoteEnter
      .append("text")
      // NOTE: use item's type to determine the correct text, e.g., metric or goal
      .text(localize("private_goal"))
      .attr("class", (d) => cssClass({ "private-badge": d.data.obfuscated, "private-text": !d.data.obfuscated }));
    privateNoteEnter
      .append("circle")
      .attr("r", 7)
      .attr("cy", 0)
      .attr("cx", -1)
      .classed("private-dot", true)
      .attr("opacity", (d) => (d.data.obfuscated ? 0 : null));

    privateNote.merge(privateNoteEnter).select("text.private-badge").attr("transform", `translate(${this.constants.xTitle}, 27)`);

    privateNote.exit().remove();
  }

  private configureIcon(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    enter
      .append("image")
      .classed("objective-icon", true)
      .attr("width", 16)
      .attr("height", 16)
      .attr("transform", (d) => `translate( 15, ${d.data.obfuscated ? 15 : 19})`);

    const icons = node.select("image.objective-icon").style("display", this.viewMode === "relaxed" || "block");

    icons
      .filter((d, i, nodes) => {
        const prevUpdate = itemIconCache.get(<Element>nodes[i]);
        return !prevUpdate || d.data.type !== prevUpdate.args[0];
      })
      .each((d, i, nodes) => {
        itemIconCache.set(<Element>nodes[i], { args: [d.data.type] });
      })
      .attr("xlink:href", (d) => `/dist/img/icons/${d.data.type}.svg`);
  }

  private configureAttainment(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const textAttainment = node
      .selectAll("text.attainment")
      .selection()
      .data((d) => (typeof d.data.attainment.value === "number" ? [d.data.attainment] : []));

    const textAttainmentEnter = textAttainment
      .enter()
      .append("text")
      .classed("attainment", true)
      .text((d) => percentageText(d.value));

    textAttainment.exit().remove();

    textAttainment
      .merge(textAttainmentEnter)
      .attr("x", this.constants.itemWidth - 40)
      .attr("y", 32)
      .attr("fill", (d) => d.color)
      .style("text-anchor", "end")
      .call(this.tooltipRight.bind(this), "progress-tooltip", localize("progress_cap"))
      // only run animation for new values
      .filter(function (d) {
        const el = this as SVGTextElement;
        return percentageText(d.value) !== el.textContent;
      })
      .text((d) => percentageText(d.value))
      .transition()
      .duration(animationDuration)
      .style("fill", "red")
      .style("text-shadow", "2px 2px 5px red")
      .transition()
      .duration(animationDuration)
      .style("fill", (d) => d.color)
      .style("text-shadow", null);

    const colorAttainment = node
      .selectAll("circle.attainment")
      .selection()
      .data((d) => (typeof d.data.attainment.value === "undefined" ? [d.data] : []));

    const colorAttainmentEnter = colorAttainment.enter().append("circle").classed("attainment", true);

    colorAttainment.exit().remove();

    colorAttainment
      .merge(colorAttainmentEnter)
      .attr("cx", this.constants.itemWidth - 25 - this.constants.progressLabelOffsetPositionX - attainmentCircleRadius)
      .attr("cy", (d) => (d.obfuscated ? this.constants.progressLabelPositionY.obfcustateObjective : this.constants.progressLabelPositionY.objective))
      .attr("r", attainmentCircleRadius)
      .attr("fill", (d) => d.attainment.color)
      .call(this.tooltipRight.bind(this), "progress-tooltip", localize("progress_cap"));
  }

  private configureAggregatedAttainment(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const textAggregatedAttainment = node
      .selectAll("text.aggregated-attainment")
      .data((d) => (this.aggregatedAttainmentVisible && d.data.supporters.length && typeof d.data.fullAggregatedAttainment.value === "number" ? [d] : []));

    const textAggregatedAttainmentEnter = textAggregatedAttainment
      .enter()
      .append("text")
      .classed("aggregated-attainment", true)
      .attr("y", -5)
      .attr("fill", (d) => d.data.fullAggregatedAttainment.color)
      .text((d) => aggregatedAttainmentText(d.data))
      .call(this.tooltipRight.bind(this), "progress-tooltip", localize("aggregated_attainment"));

    textAggregatedAttainment.exit().remove();

    textAggregatedAttainment
      .merge(textAggregatedAttainmentEnter)
      .attr("x", this.constants.itemWidth + 10)
      // only run animation for new values
      .filter(function (d) {
        const el = this as SVGTextElement;
        return aggregatedAttainmentText(d.data) !== el.textContent;
      })
      .text((d) => aggregatedAttainmentText(d.data))
      .transition()
      .duration(animationDuration)
      .style("fill", "red")
      .style("text-shadow", "2px 2px 5px red")
      .transition()
      .duration(animationDuration)
      .style("fill", (d) => d.data.fullAggregatedAttainment.color)
      .style("text-shadow", null);

    const colorAggregatedAttainment = node
      .selectAll("circle.aggregated-attainment")
      .data((d) => (this.aggregatedAttainmentVisible && d.data.supporters.length && typeof d.data.fullAggregatedAttainment.value === "undefined" ? [d] : []));

    const colorAggregatedAttainmentEnter = colorAggregatedAttainment.enter().append("circle").classed("aggregated-attainment", true);

    colorAggregatedAttainment.exit().remove();

    colorAggregatedAttainment
      .merge(colorAggregatedAttainmentEnter)
      .attr("cx", this.constants.itemWidth + 15)
      .attr("cy", -9)
      .attr("r", attainmentCircleRadius)
      .attr("fill", (d) => d.data.fullAggregatedAttainment.color)
      .call(this.tooltipRight.bind(this), "progress-tooltip", localize("aggregated_attainment"));
  }

  private configureContextMenu(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    enter
      .append("text")
      .classed("dropdown-toggler", true)
      .text("...")
      .on("click", (evt: MouseEvent, d) => this.contextMenuVisible(d.data) && this.dispatchAndStopPropagation("contextMenuOpen", evt, d.data));

    node
      .select(".dropdown-toggler")
      .attr("transform", (d) => `translate(${this.constants.itemWidth - 30}, ${d.data.obfuscated ? 24 : 28})`)
      .attr("opacity", (d) => (this.contextMenuVisible(d.data) ? 1 : 0))
      .attr("data-test-id", "alignment-tree-objective-context-menu");
  }

  private configureItemOwnersNames(node: MyHierarchyPointNodeSelection<IItem>) {
    const ownersNames = node.selectAll("text.item-owners-names").data((d) => (d.data.owners ? [d] : []));

    const ownerEnter = ownersNames.enter().append("text").classed("item-owners-names", true);

    ownersNames
      .merge(ownerEnter)
      .text((d) => {
        const names = [];
        const namesMaxLength = 35;

        const appendOwnerName = (owner: OwnerToPreview): void => {
          if (isDeletedAssignee(owner)) {
            return;
          }
          names.push(owner.isActive === false ? `${owner.name} (${localize("deactivated")})` : owner.name);
        };

        d.data.owners.preview.forEach(appendOwnerName);

        if (names.join(", ").length < namesMaxLength) {
          d.data.owners.additional.forEach(appendOwnerName);
        }

        let namesAsStr = names.join(", ");
        if (namesAsStr.length > namesMaxLength) {
          namesAsStr = namesAsStr.slice(0, namesMaxLength) + "...";
        }
        return namesAsStr;
      })
      .attr("transform", (d) => {
        const x = this.constants.xTitle;
        const topOffsetFromItemTitle = 18;
        const y = d.title.lines * this.constants.itemTitleStyles.lineHeight + this.constants.ownersNamesHeight + topOffsetFromItemTitle;
        return `translate(${x}, ${y})`;
      });

    ownersNames.exit().remove();
  }

  private configureContributorOwnersIcons(node: MySelection<IContributorSubnode>) {
    let iconsContainer = node.selectAll("g.owners").data((d) => {
      return d.contributor ? [d.contributor] : [];
    });
    const iconsEnter = iconsContainer.enter().append("g").classed("owners", true);
    iconsContainer.exit().remove();

    iconsContainer = iconsContainer.merge(iconsEnter);

    this.configureOwnersIconsContents(iconsContainer, { contributor: true });
  }

  private configureItemOwnersIcons(node: MyHierarchyPointNodeSelection<IItem>) {
    let iconsContainer = node.selectAll("g.owners").data((d) => {
      return d.data ? [d.data] : [];
    });
    const iconsEnter = iconsContainer.enter().append("g").classed("owners", true);
    iconsContainer.exit().remove();

    iconsContainer = iconsContainer.merge(iconsEnter);

    this.configureOwnersIconsContents(iconsContainer, { contributor: false });
  }

  private configureOwnersIconsContents(iconsContainer: Selection<BaseType, IItem, BaseType, unknown>, opts: { contributor: boolean }) {
    const iconAvatarWidth = opts.contributor ? this.constants.contributorAvatarWidth : this.constants.itemAvatarWidth;

    const getIconX = (i: number, iconsCount: number) => {
      const contributorPositionCorrection = opts.contributor ? 10 : 0;
      const iconsPadEndRightOffset = 95;
      const iconsWidth = iconAvatarWidth + (iconAvatarWidth - 2) * (iconsCount - 1);

      return this.constants.itemWidth - iconsPadEndRightOffset - iconsWidth + i * (iconAvatarWidth - 2) - contributorPositionCorrection;
    };
    const getIconY = () => (opts.contributor ? 3 : 27);

    this.configureOwnersIconsWithPicture(iconsContainer, { ...opts, getIconY, getIconX, iconAvatarWidth });
    this.configureOwnersIconsWithoutPicture(iconsContainer, { ...opts, getIconY, getIconX, iconAvatarWidth });
    this.configureAdditionalOwnersIcon(iconsContainer, { ...opts, getIconY, getIconX, iconAvatarWidth });

    iconsContainer.selectAll("circle").sort((a: OwnerToPreview, b: OwnerToPreview) => ascending(a.index, b.index));
  }

  private configureOwnersIconsWithPicture(
    iconsContainer: Selection<BaseType, IItem, BaseType, unknown>,
    opts: { contributor: boolean; getIconY(): number; getIconX(i: number, iconsCount: number): number; iconAvatarWidth: number }
  ) {
    const defs = iconsContainer.append("defs");
    defs.exit().remove();

    const pattern = defs.selectAll("pattern").data((d) => d.owners.preview.filter((o) => isDeletedAssignee(o) || o.picture));

    const patternEnter = pattern.enter().append("pattern");

    pattern.exit().remove();

    pattern
      .merge(patternEnter)
      .attr("id", (d) => `avatar${d.index}${opts.contributor ? "c" : ""}${d.id}`)
      .attr("width", opts.iconAvatarWidth + 2)
      .attr("height", opts.iconAvatarWidth + 2)
      .append("image")
      .attr("xlink:href", (d) => (isDeletedAssignee(d) ? deletedAvatarURL : d.picture))
      .attr("width", opts.iconAvatarWidth + 2)
      .attr("height", opts.iconAvatarWidth + 2);

    const ownerWithPicture = iconsContainer
      .selectAll("circle.owner-with-picture")
      .selection()
      .data((d) => d.owners.preview.filter((o) => isDeletedAssignee(o) || o.picture));

    const ownerWithPictureEnter = ownerWithPicture.enter().append("circle");

    ownerWithPicture.exit().remove();

    ownerWithPicture
      .merge(ownerWithPictureEnter)
      .attr("r", opts.iconAvatarWidth / 2)
      .style("stroke", "white")
      .style("stroke-width", 1.6)
      .attr("transform", (d) => `translate(${opts.getIconX(d.index, d.iconsCount)}, ${opts.getIconY()})`)
      .style("fill", (d) => `url(#avatar${d.index}${opts.contributor ? "c" : ""}${d.id})`)
      .style("border-radius", "100%")
      .style("opacity", (d) => (!isDeletedAssignee(d) && d.isActive === false ? 0.4 : ""))
      .attr("class", (d) =>
        cssClass({
          "assignee-picture": !isDeletedAssignee(d) && d.type === "user",
          deactivated: !isDeletedAssignee(d) && d.isActive === false,
          "team-owner": !isDeletedAssignee(d) && d.type === "team",
          "owner-with-picture": true,
        })
      )
      .call(this.tooltip.bind(this), "owner-name-tooltip", (evt: MouseEvent, d: OwnerToPreview) => {
        const { x: left, bottom } = select(evt.target as Element)
          .node()
          .getBoundingClientRect();

        const x = left + opts.iconAvatarWidth / 2;
        const y = bottom + 5;

        if (isDeletedAssignee(d)) {
          return { x, y, html: `<div class="secondary-info">(${localize("deleted_user")})</div>`, centerTooltip: true };
        }
        if (d.isActive === false) {
          return { x, y, html: `${d.name}<div class="secondary-info">(${localize("deactivated")})</div>`, centerTooltip: true };
        }
        return { x, y, text: d.name, centerTooltip: true };
      });
  }

  private configureOwnersIconsWithoutPicture(
    iconsContainer: Selection<BaseType, IItem, BaseType, unknown>,
    opts: { contributor: boolean; getIconY(): number; getIconX(i: number, iconsCount: number): number; iconAvatarWidth: number }
  ) {
    const deletedOwnersFilterFn = (o: IAssignee | IDeletedAssignee) => !isDeletedAssignee(o) && !o.picture;

    const ownerWithoutPictureColor = iconsContainer.selectAll("circle.owner-without-picture").data((d) => d.owners.preview.filter(deletedOwnersFilterFn));
    const ownerWithoutPictureColorEnter = ownerWithoutPictureColor.enter().append("circle").classed("owner-without-picture", true);

    ownerWithoutPictureColor
      .merge(ownerWithoutPictureColorEnter)
      .attr("r", opts.iconAvatarWidth / 2)
      .attr("transform", (d) => `translate(${opts.getIconX(d.index, d.iconsCount)}, ${opts.getIconY()})`)
      .style("stroke", "white")
      .style("stroke-width", 1.6)
      .style("opacity", (d) => (!isDeletedAssignee(d) && d.isActive === false ? 0.4 : ""))
      .attr("fill", (d) => (isDeletedAssignee(d) ? "#000000" : d.color))
      .call(this.tooltip.bind(this), "owner-name-tooltip", (evt: MouseEvent, d: OwnerToPreview) => {
        const { left, bottom } = select(evt.target as Element)
          .node()
          .getBoundingClientRect();

        const x = left + opts.iconAvatarWidth / 2;
        const y = bottom + 5;
        if (isDeletedAssignee(d)) {
          return { x, y, html: `<div class="secondary-info">(${localize("deleted_user")})</div>`, centerTooltip: true };
        }
        if (d.isActive === false) {
          return { x, y, html: `${d.name}<div class="secondary-info">(${localize("deactivated")})</div>`, centerTooltip: true };
        }
        return { x, y, text: d.name, centerTooltip: true };
      });
    ownerWithoutPictureColor.exit().remove();

    const ownerWithoutPictureAvatar = iconsContainer.selectAll("foreignObject").data((d) => d.owners.preview.filter(deletedOwnersFilterFn));
    const ownerWithoutPictureAvatarEnter = ownerWithoutPictureAvatar.enter().append("svg:foreignObject");
    ownerWithoutPictureAvatar
      .merge(ownerWithoutPictureAvatarEnter)
      .attr("width", opts.iconAvatarWidth - 5)
      .attr("height", opts.iconAvatarWidth - 5)
      .attr("pointer-events", "none")
      .style("font-size", () => `${opts.iconAvatarWidth / 2 + 4}px`)
      .attr("transform", (d) => `translate(${opts.getIconX(d.index, d.iconsCount) + 3 - opts.iconAvatarWidth / 2}, ${opts.getIconY() + 3 - opts.iconAvatarWidth / 2})`)
      .append("xhtml:span")
      .attr("class", (d) => `team-avatar nc-icon-outline icon ${!isDeletedAssignee(d) && d.avatar}`);
    ownerWithoutPictureAvatar.exit().remove();
  }

  private configureAdditionalOwnersIcon(
    iconsContainer: Selection<BaseType, IItem, BaseType, unknown>,
    opts: { contributor: boolean; getIconY(): number; getIconX(i: number, iconsCount: number): number; iconAvatarWidth: number }
  ) {
    const additionalOwners = iconsContainer
      .selectAll("circle.additional-owners")
      .data((d) => (d.owners.additional.length ? [{ additionalOwners: d.owners.additional, index: 2, iconsCount: 3 }] : []));

    const additionalOwnersEnter = additionalOwners.enter().append("circle").classed("additional-owners", true);

    additionalOwners.exit().remove();

    additionalOwners
      .merge(additionalOwnersEnter)
      .attr("r", opts.iconAvatarWidth / 2)
      .attr("transform", (d) => `translate(${opts.getIconX(d.index, d.iconsCount)}, ${opts.getIconY()})`)
      .attr("cursor", "pointer")
      .style("stroke", "white")
      .style("stroke-width", 1.6)
      .attr("fill", "#8393AE")
      .on("mousemove", (evt: MouseEvent, d) => {
        const { left: leftNode, bottom: bottomNode } = select(evt.target as Element)
          .node()
          .getBoundingClientRect();

        const left = leftNode + opts.iconAvatarWidth / 2;
        const top = bottomNode + 5;

        this.dispatchAndStopPropagation("additionalOwnersOpen", evt, { additionalOwners: d.additionalOwners, top, left });
      })
      .on("mouseleave", (evt: MouseEvent) => this.dispatchAndStopPropagation("additionalOwnersClose", evt));

    const additionalOwnersText = iconsContainer
      .selectAll("text.additional-owners-text")
      .selection()
      .data((d) => (d.owners.additional.length ? [{ additionalOwners: d.owners.additional }] : []));

    const additionalOwnersTextEnter = additionalOwnersText.enter().append("text").classed("additional-owners-text", true);

    additionalOwnersText.exit().remove();

    additionalOwnersText
      .merge(additionalOwnersTextEnter)
      .text((d) => {
        if (d.additionalOwners.length < 10) return `+${d.additionalOwners.length}`;

        if (d.additionalOwners.length > 99) return `+99`;

        return `${d.additionalOwners.length}`;
      })
      .attr("text-anchor", "middle")
      .attr("cursor", "pointer")
      .attr("pointer-events", "none")
      .attr("dy", () => {
        const radius = opts.iconAvatarWidth / 2;
        return radius / ((radius * 25) / 100);
      })
      .attr("transform", () => `translate(${opts.getIconX(2, 3)}, ${opts.getIconY()})`)
      .style("font-size", "12px")
      .style("font-weight", "500")
      .attr("fill", "white");
  }

  private configurePad(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    const width = this.constants.itemWidth;

    enter
      .append("rect")
      .classed("pad", true)
      .attr("node", (d) => d.id)
      .attr("y", 0)
      .attr("x", 0)
      .attr("rx", 5)
      .attr("ry", 5);

    node
      .select("rect.pad")
      .attr("width", width)
      .attr("height", (d) => this.padHeight(d))
      .attr("data-test-id", "alignment-tree-pad");
  }

  private configureSessionColorIndicator(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    enter
      .append("rect")
      .classed("session-color", true)
      .attr("node", (d) => d.id)
      .attr("y", 0)
      .attr("x", 0)
      .attr("rx", 5)
      .attr("ry", 5)
      .attr("width", 2);

    node
      .select("rect.session-color")
      .attr("height", (d) => this.padHeight(d))
      .attr("fill", (d) => d.data.session.color);
  }

  private configureSessionColorOverlay(enter: MyHierarchyPointNodeSelection<IItem>, node: MyHierarchyPointNodeSelection<IItem>) {
    enter
      .append("rect")
      .classed("session-color-overlay", true)
      .attr("node", (d) => d.id)
      .attr("y", 0)
      .attr("x", 0)
      .attr("rx", 5)
      .attr("ry", 5);

    node
      .select("rect.session-color-overlay")
      .style("display", this.expandSessionColorIndicator() ? "block" : "none")
      .attr("width", this.constants.itemWidth)
      .attr("height", (d) => this.padHeight(d))
      .attr("fill", (d) => d.data.session.color);
  }

  private padHeight(d: MyHierarchyPointNode<IItem>) {
    const visibleContributors = this.getVisibleContributors(d);

    const visibleContributorsCount = visibleContributors.length;

    let visibleContributorsAdditionalTitleRows = 0;
    const additionalItemTitleLines = d.data.obfuscated ? 0 : Math.ceil(d.title.lines - 1);

    for (const contributor of visibleContributors) {
      if (!contributor.obfuscated) {
        const wrappedTitle = this.wrapTitle(contributor, true);
        visibleContributorsAdditionalTitleRows += wrappedTitle.lines - 1;
      }
    }

    const nodeContentVerticalOffset = d.data.obfuscated ? 0 : this.constants.contentHeightOffset * 2;
    const ownersNamesHeight = d.data.obfuscated ? 0 : this.constants.ownersNamesHeight;

    return (
      this.constants.rowHeight +
      additionalItemTitleLines * this.constants.itemTitleStyles.lineHeight +
      ownersNamesHeight +
      visibleContributorsCount * this.constants.rowHeight +
      visibleContributorsAdditionalTitleRows * this.constants.contributorTitleStyles.lineHeight +
      nodeContentVerticalOffset
    );
  }

  private getVisibleContributors(d: MyHierarchyPointNode<IItem>): IItem[] {
    return this.constants.showContributors
      ? this.constants.showOnlyContributorsOfTypeGoal
        ? d.data.contributors.filter((c) => c.type === "goal" || (c.type === "metric" && c.supporters.length))
        : d.data.contributors
      : [];
  }

  private itemPadHeight(d: MyHierarchyPointNode<IItem>) {
    if (d.data.obfuscated) {
      return this.constants.rowHeight + 6; // 6 = added height so the whole item is highlighted when shared
    }

    const additionalItemTitleLines = Math.ceil(d.title.lines - 1);
    return (
      this.constants.rowHeight +
      this.constants.contentHeightOffset * 2 +
      additionalItemTitleLines * this.constants.itemTitleStyles.lineHeight +
      this.constants.ownersNamesHeight
    );
  }

  private contributorPadHeight(d: IContributorSubnode) {
    if (d.contributor.obfuscated) {
      return this.constants.rowHeight;
    }

    const titleWrap = this.wrapTitle(d.contributor, true);
    const offset = ((titleWrap.overflow ? this.constants.titleWrap.maxLines : titleWrap.lines) - 1) * this.constants.contributorTitleStyles.lineHeight;
    const filterBorderWidth = 3; // border width when the contributor matches filter
    return this.constants.rowHeight + offset - filterBorderWidth;
  }

  private selectContributorWithinItemNode(id: IItemId): Selection<SVGGElement, IContributorSubnode, BaseType, unknown> {
    return select(`#${contributorId(id)}`);
  }

  private selectItemNode(id: IItemId): Selection<SVGGElement, MyHierarchyPointNode<IItem>, BaseType, unknown> {
    return select(`#${itemNodeId(id)}`);
  }

  private expandSessionColorIndicator(): boolean {
    // when the nodes get too small they are covered with the session's color box
    // else session's color box occupies left side of goal container resembling a line
    return this.zoomRatio < 0.5;
  }

  private wrapTitle(item: IItem | IShowMoreNode, isContributor: boolean): IMyHierarchyPointNodeTitle {
    const prevExecution = this.wrapCache[item.id];
    if (prevExecution && item.title === prevExecution.args[0] && this.constants == prevExecution.args[1]) {
      return prevExecution.result;
    }

    const constants = this.getWrapTitleConstants(item, isContributor);
    const result = wrapTitle(item.title, constants);
    this.wrapCache[item.id] = { args: [item.title, this.constants], result };
    return result;
  }

  private getWrapTitleConstants(item: IItem | IShowMoreNode, isContributor: boolean): WrapTitleConstants {
    const { lineLength, maxLines } = this.constants.titleWrap;
    const { fontSize, fontWeight, lineHeight } = isContributor ? this.constants.contributorTitleStyles : this.constants.itemTitleStyles;

    return {
      lineLength,
      maxLines,
      fontSize,
      fontWeight,
      lineHeight,
      titleWidth: this.titleWidth(item, isContributor),
    };
  }

  private titleWidth = (item: IItem | IShowMoreNode, isContributor: boolean): string => {
    if (item.type !== "show-more") {
      const iconAvatarWidth = isContributor ? this.constants.contributorAvatarWidth : this.constants.itemAvatarWidth;
      const iconsCount = item.owners?.preview[0]?.iconsCount || 0;
      const offsetCount = 3 - iconsCount;

      return `${this.constants.titleWidth + offsetCount * iconAvatarWidth}px`;
    } else {
      return `${this.constants.titleWidth}px`;
    }
  };

  public getNumberOfTitleLines(id: IItemId): number {
    return (this.wrapCache[id.id].result as IMyHierarchyPointNodeTitle).lines;
  }

  public getContributorTitleLineHeight(): number {
    return this.constants.contributorTitleStyles.lineHeight;
  }

  private tooltipRight<GElement extends BaseType, Datum, PElement extends BaseType, PDatum>(
    selection: Selection<GElement, Datum, PElement, PDatum>,
    cssClass: string,
    text: string | ((d: Datum) => string)
  ) {
    this.tooltip<GElement, Datum, PElement, PDatum>(selection, cssClass, (evt: MouseEvent, d: Datum) => {
      // the unnamed constants are taken with try-and-error to make it look nice
      const x = evt.pageX + 50;
      const y = evt.pageY - 12;
      return { x, y, text: typeof text === "string" ? text : text(d), hideTooltip: false, centerTooltip: false };
    });
  }

  private tooltip<GElement extends BaseType, Datum, PElement extends BaseType, PDatum>(
    selection: Selection<GElement, Datum, PElement, PDatum>,
    cssClass: string,
    data: (evt: MouseEvent, d: Datum) => { x: number; y: number; text: string; html?: string; hideTooltip: boolean; centerTooltip: boolean }
  ) {
    selection
      .on("mousemove", (evt: MouseEvent, d: Datum) => {
        const { y, text, html, hideTooltip, centerTooltip } = data(evt, d);
        let { x } = data(evt, d);
        if (hideTooltip) {
          return;
        }
        let tooltip = select(`.alignment-tree-tooltip.${cssClass}`);
        if (!tooltip.node()) {
          tooltip = select("body").append("div").classed("alignment-tree-tooltip", true).classed(cssClass, true);
        }

        if (centerTooltip) {
          const tooltipWidth = (<Element>tooltip.node())?.getBoundingClientRect()?.width || 0;

          x = x - tooltipWidth / 2;
        }

        if (html) {
          tooltip.html(html).style("left", `${x}px`).style("top", `${y}px`);
        } else {
          tooltip.text(text).style("left", `${x}px`).style("top", `${y}px`);
        }
      })
      .on("mouseout", () => select(`.alignment-tree-tooltip.${cssClass}`).remove());
  }

  private initZoom() {
    return zoom()
      .scaleExtent([this.zoomRange.min, this.zoomRange.max])
      .on("zoom", (evt: D3ZoomEvent<Element, unknown>) => {
        const zoomChanged = this.zoomRatio !== evt.transform.k;
        this.zoomRatio = evt.transform.k;
        this.mainGroup.attr("transform", evt.transform.toString());

        if (this.dragOrPointerNavigationEnabled) {
          // Save the value of current mainGroup transform
          this.currentScroll = {
            left: evt.transform.x,
            top: evt.transform.y,
          };

          this.dispatch("zoom", evt.sourceEvent as Event, this.zoomRatio);

          // recreate tree only if there is zoom ratio change
          if (zoomChanged) {
            this.createTree({ hierarchyUpdated: false, itemsUpdated: false });
            this.mainGroup.classed("simplified", () => this.expandSessionColorIndicator()).classed("enabledDrag", this.itemRearrangeEnabled);
          }
        } else {
          this.dispatch("zoom", evt.sourceEvent as Event, null);
          this.renderInternal({ hierarchyUpdated: false, itemsUpdated: false });
        }
      })
      .filter((e) => {
        // Filter wheel events with no metaKey(cmd/ctrl) pressed
        return !this.dragOrPointerNavigationEnabled || !(e && e.type === "wheel" && !e.metaKey);
      });
  }

  private findContributorCoordinates(node: MyHierarchyPointNode<IItem>, id: IItemId): ICoordinates {
    const contributors = this.getVisibleContributors(node);
    let previousAdditionalTitleLines = 0;
    let contributorIndex;

    for (let index = 0; index < contributors.length; index++) {
      const c = contributors[index];

      if (itemEquals(c, id)) {
        contributorIndex = index;
        break;
      }

      const wrappedTitle = this.wrapTitle(c, true);
      previousAdditionalTitleLines += c.obfuscated ? 0 : Math.ceil(wrappedTitle.lines - 1);
    }

    if (contributorIndex < 0) {
      return null;
    }

    const coords = { x: node.x, y: node.y };

    coords.x += this.constants.rowHeight; //for parent node
    const additionalItemTitleLines = node.data.obfuscated ? 0 : Math.ceil(node.title.lines - 1);
    coords.x += additionalItemTitleLines * this.constants.itemTitleStyles.lineHeight;
    coords.x += contributorIndex * this.constants.rowHeight; // move further down depending on which contributor it comes from
    coords.x += previousAdditionalTitleLines * this.constants.contributorTitleStyles.lineHeight;
    coords.x += node.data.obfuscated ? 0 : this.constants.ownersNamesHeight;

    return coords;
  }

  private findCrossContributorLinkEnd(thisContributor: IItem, thisLink: ICrossContributorLink): ICrossContributorLinkEnd {
    const otherContributorNode = this.selectContributorWithinItemNode(thisLink);
    if (!otherContributorNode.node()) {
      return null;
    }

    const { node: otherNode, contributor: otherContributor } = otherContributorNode.datum();

    const otherLink = otherContributor.crossContributorLinks.find((l) => itemEquals(l, thisContributor));
    if (!otherLink) {
      console.warn("Weird! We have found the contributor, but not a corresponding link", { thisContributor, thisLink, otherContributor });
      return null;
    }

    return {
      node: otherNode,
      contributor: otherContributor,
      link: otherLink,
    };
  }

  /**
   * Cross-contributor links are declared in both contributors with different
   * contribution value and link type (inbound vs outbound). This function merges
   * these declarations in a way that we have the link as one entity with both
   * source and target. It is possible that either the source or the target are missing, e.g.,
   * when one of them is in a session that is not visible now.
   */
  private mergeCrossContributorLinks(node: MyHierarchyPointNode<IItem>, contributor: IItem): IMergedCrossContributorLink[] {
    const linkId = (source: ICrossContributorLinkEnd, target: ICrossContributorLinkEnd) =>
      (source ? `${source.contributor.type}-${source.contributor.id}-` : "") + (target ? `-${target.contributor.type}-${target.contributor.id}` : "");

    const map: { [key: string]: IMergedCrossContributorLink } = {};

    const visit = (n: MyHierarchyPointNode<IItem>, c: IItem): void => {
      for (const l of c.crossContributorLinks) {
        const isLinkToItself = itemEquals(c, l);
        if (isLinkToItself) {
          console.warn("Links cannot point to itself", c, l);
          continue;
        }

        // links have direction, so to avoid confusion we use the terms here/there
        // to denote this current link (here) and the other end of the link (there)
        const here: ICrossContributorLinkEnd = { node: n, contributor: c, link: l };
        const there = this.findCrossContributorLinkEnd(c, l);

        const source = l.direction === "outbound" ? here : there;
        const target = l.direction === "outbound" ? there : here;
        const id = linkId(source, target);

        // no duplicate links
        if (map[id]) {
          continue;
        }

        map[id] = { source, target };

        if (there) {
          visit(there.node, there.contributor);
        }
      }
    };
    visit(node, contributor);

    return Object.values(map);
  }

  /**
   * A curve where the source is the item node or one of its contributors
   */
  private itemNodeOrContributorCurve(d: HierarchyPointLink<Node>): string {
    let source: ICoordinates = d.source;
    const target: ICoordinates = d.target;

    // adjust the link, if the item is a child of a contributor of another item

    // the target could not be the root in reality, nor can a show-more node be the source,
    // but the checks are added so TypeScript can detect the proper typings
    const sourceItem = d.source.data;
    const targetItem = d.target.data;
    if (sourceItem.type !== "root" && sourceItem.type !== "show-more" && targetItem.type !== "root" && targetItem.parent) {
      const isTargetNodeSupporterOfContributorOfItemNode = targetItem.type !== "show-more" && !itemEquals(sourceItem, targetItem.parent);
      const isTargetNodeShowMore = targetItem.type === "show-more";

      if (isTargetNodeSupporterOfContributorOfItemNode) {
        const targetItemParent = targetItem.parent as IItemId;
        const contributor = sourceItem.contributors.find((c) => itemEquals(c, targetItemParent));
        source = this.findContributorCoordinates(d.source as MyHierarchyPointNode<IItem>, contributor);
      } else if (isTargetNodeShowMore) {
        const targetItemParent = targetItem.parent as IItemId;
        const isSourceNodeContributorOfItemNode = !itemEquals(sourceItem, targetItemParent);
        if (isSourceNodeContributorOfItemNode) {
          const contributor = sourceItem.contributors.find((c) => itemEquals(c, targetItemParent));
          source = this.findContributorCoordinates(d.source as MyHierarchyPointNode<IItem>, contributor);
        }
      } else {
        const targetItemParent = targetItem.parent as IItemParent;
        const isTargetNodeContributorOfItemNode = itemEquals(sourceItem, targetItemParent) && targetItemParent.relation === "contribution";
        if (isTargetNodeContributorOfItemNode) {
          source = this.findContributorCoordinates(d.source as MyHierarchyPointNode<IItem>, targetItem as IItem);
        }
      }
    }

    return this.expanderSourceNodeCurve(source, target);
  }

  /**
   * A general curve between two nodes where the source begins
   * at the expand toggle's coordinates
   */
  private expanderSourceNodeCurve(source: ICoordinates, target: ICoordinates): string {
    const expanderWidthIncludingSpacing = 25;
    const adjustedSource: ICoordinates = {
      x: source.x,
      y: source.y + expanderWidthIncludingSpacing,
    };
    return this.nodeCurve(adjustedSource, target);
  }

  /**
   * A general curve between two nodes
   */
  private nodeCurve(source: ICoordinates, target: ICoordinates) {
    const rowMiddle = this.constants.rowHeight / 2;
    const sx = source.y + this.constants.itemWidth;
    const sy = source.x + rowMiddle;
    const tx = target.y;
    const ty = target.x + rowMiddle;
    const delta = this.constants.linkWidth / 2;

    return `M ${sx} ${sy} C ${sx + delta} ${sy}, ${tx - delta} ${ty}, ${tx} ${ty}`;
  }

  private createShowMoreNode(parent: { id: string; type: RootNodeType | ItemType; contributor?: IItem }, hiddenItemsCount: number): IShowMoreNode {
    let title = `••• ${localize("show")} ${hiddenItemsCount} ${localize("more")}`;

    if (!parent?.contributor?.matchesFilter) {
      title = `••• ${hiddenItemsCount} ${localize("more")}`;
    }

    return {
      id: `show-more-${parent.type}-${parent.id}`,
      type: "show-more",
      title,
      parent,
    };
  }
}
